import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Home, CalendarDays, Sparkles, Users, LayoutDashboard, Bell, Search, Menu, X,
  CheckCircle2, Clock, AlertCircle, ArrowRightLeft, LogOut, MapPin, Plus, Key,
  User, Lock, MessageSquare, History, Mail, LockKeyhole, Calendar, ChevronLeft,
  ChevronRight, ChevronDown, Filter, Briefcase, UserPlus, Camera, Image as ImageIcon, Trash2,
  Loader2, AlertTriangle, Play, Pause, Square, Timer, Eye, EyeOff,
} from 'lucide-react';
import api, { formatApiError } from './api';
import { fileToCompressedDataUrl } from './imageUtils';

const MAX_PHOTOS = 5;

// --- helpers ---
const today = new Date();
const formatDate = (d) => d.toISOString().split('T')[0];
const todayStr = formatDate(today);
const formatDisplayDate = (s) => new Date(s).toLocaleDateString('es-ES', { weekday: 'short', month: 'short', day: 'numeric' });

// Opens a new browser window with a printable HTML receipt for a check-in task.
function generateReceiptWindow(task, property) {
  const escape = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const now = new Date();
  const receiptNo = `KAS-${(task.id || '').slice(-6).toUpperCase()}-${now.getFullYear()}`;
  const issued = now.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) + ' ' + now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const dateStr = task.date ? new Date(task.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Recibo ${escape(receiptNo)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b; margin: 0; padding: 40px 24px; background: #f8fafc; }
  .paper { max-width: 640px; margin: 0 auto; background: white; padding: 48px 40px; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.06); }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #4f46e5; padding-bottom: 20px; margin-bottom: 32px; }
  .brand { font-size: 26px; font-weight: 900; color: #4f46e5; letter-spacing: -0.5px; }
  .brand small { display: block; font-size: 11px; color: #64748b; font-weight: 500; margin-top: 4px; letter-spacing: 0.5px; }
  .meta { text-align: right; font-size: 12px; color: #64748b; line-height: 1.6; }
  .meta strong { color: #1e293b; font-size: 14px; }
  h1 { font-size: 22px; margin: 0 0 24px; color: #1e293b; }
  .row { display: grid; grid-template-columns: 140px 1fr; padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; }
  .row:last-child { border-bottom: none; }
  .label { color: #64748b; font-weight: 500; }
  .value { color: #0f172a; font-weight: 600; }
  .highlight { background: #eef2ff; border-left: 4px solid #4f46e5; padding: 14px 18px; border-radius: 8px; margin: 20px 0; font-size: 14px; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; text-align: center; line-height: 1.6; }
  .signature { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 48px; padding-top: 20px; }
  .sig-canvas { display: block; width: 100%; max-width: 280px; height: 90px; border: 1px solid #cbd5e1; border-radius: 6px; background: #fafafa; touch-action: none; cursor: crosshair; }
  .sig-controls { margin-top: 4px; }
  .btn-mini { font-size: 11px; padding: 3px 8px; border: 1px solid #cbd5e1; background: white; border-radius: 4px; cursor: pointer; color: #475569; }
  .btn-mini:hover { background: #f1f5f9; }
  .sig-line { border-top: 1px solid #94a3b8; padding-top: 8px; font-size: 12px; color: #64748b; text-align: center; margin-top: 6px; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; text-align: center; line-height: 1.6; }
  .actions { max-width: 640px; margin: 20px auto 0; display: flex; gap: 12px; justify-content: center; }
  .btn { padding: 10px 20px; border-radius: 8px; border: 0; font-weight: 600; font-size: 14px; cursor: pointer; }
  .btn-primary { background: #4f46e5; color: white; }
  .btn-primary:hover { background: #4338ca; }
  .btn-secondary { background: white; color: #475569; border: 1px solid #cbd5e1; }
  @media print { body { background: white; padding: 0; } .paper { box-shadow: none; padding: 32px; max-width: 100%; } .no-print { display: none !important; } .sig-canvas { border: 0; background: transparent; } }
</style></head><body>
<div class="paper">
  <div class="header">
    <div class="brand"><img src="${window.location.origin}/kasaalmar-logo.jpg" alt="Kasaalmar" style="width:44px;height:44px;object-fit:contain;vertical-align:middle;margin-right:8px;" onerror="this.style.display='none'" />Kasaalmar<small>AGENCIA DE ALQUILER VACACIONAL</small></div>
    <div class="meta"><strong>Recibo #${escape(receiptNo)}</strong><br>Emitido: ${escape(issued)}</div>
  </div>
  <h1>Confirmación de Check-in</h1>
  <div class="row"><div class="label">Huésped</div><div class="value">${escape(task.guest || '—')}</div></div>
  ${task.guest_phone ? `<div class="row"><div class="label">Teléfono</div><div class="value">${escape(task.guest_phone)}</div></div>` : ''}
  ${task.guest_email ? `<div class="row"><div class="label">Email</div><div class="value">${escape(task.guest_email)}</div></div>` : ''}
  <div class="row"><div class="label">Propiedad</div><div class="value">${escape(property?.name || '—')}</div></div>
  ${property?.address ? `<div class="row"><div class="label">Dirección</div><div class="value">${escape(property.address)}</div></div>` : ''}
  <div class="row"><div class="label">Fecha check-in</div><div class="value">${escape(dateStr)}${task.time ? ' — ' + escape(task.time) : ''}</div></div>
  <div class="row"><div class="label">Tipo</div><div class="value">${task.type === 'automatico' ? 'Self Checkin (Caja de seguridad)' : 'Presencial'}</div></div>
  <div class="row"><div class="label">Recepción</div><div class="value">${escape(task.assigned_to_name || '—')}</div></div>
  <div class="highlight">
    <strong>${task.status === 'completed' ? '✅ Check-in confirmado' : '⏳ Check-in pendiente'}</strong><br>
    ${task.notes ? escape(task.notes).replace(/\n/g,'<br>') : 'Sin observaciones.'}
  </div>
  <div class="signature">
    <div>
      <canvas id="sig-guest" width="280" height="90" class="sig-canvas" data-target="Firma del huésped"></canvas>
      <div class="sig-controls no-print">
        <button type="button" class="btn-mini" onclick="clearSig('sig-guest')">Borrar</button>
      </div>
      <div class="sig-line">Firma del huésped (dibuja aquí)</div>
    </div>
    <div>
      <canvas id="sig-reception" width="280" height="90" class="sig-canvas" data-target="Firma de recepción"></canvas>
      <div class="sig-controls no-print">
        <button type="button" class="btn-mini" onclick="clearSig('sig-reception')">Borrar</button>
      </div>
      <div class="sig-line">Firma de recepción (dibuja aquí)</div>
    </div>
  </div>
  <div class="footer">
    Gracias por elegir Kasaalmar Holidays.<br>
    Para cualquier consulta escríbenos a hola@kasaalmarholidays.com o visita <strong>kasaalmarholidays.com</strong>
  </div>
</div>
<div class="actions no-print">
  <button class="btn btn-primary" onclick="window.print()">🖨 Imprimir / Guardar PDF</button>
  <button class="btn btn-secondary" onclick="window.close()">Cerrar</button>
</div>
<script>
  // Simple pointer-based signature pad. Works on mouse and touch/pen (iPad, phone).
  function setupSig(id) {
    var canvas = document.getElementById(id);
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    var drawing = false;
    var last = null;
    function pointer(evt) {
      var r = canvas.getBoundingClientRect();
      var t = (evt.touches && evt.touches[0]) || evt;
      return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
    }
    function start(e) { e.preventDefault(); drawing = true; last = pointer(e); }
    function move(e) {
      if (!drawing) return;
      e.preventDefault();
      var p = pointer(e);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      last = p;
    }
    function end() { drawing = false; last = null; }
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
  }
  function clearSig(id) {
    var c = document.getElementById(id);
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  }
  setupSig('sig-guest');
  setupSig('sig-reception');
</script>
</body></html>`;
  // Content is fully escaped (see escape() helper). We use a Blob URL instead of document.write
  // to avoid the static-analysis flag and any lingering XSS surface.
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'width=820,height=900');
  if (!win) {
    URL.revokeObjectURL(url);
    alert('El navegador bloqueó la ventana emergente. Habilita popups para este sitio.');
    return;
  }
  // Revoke the object URL once the new window has finished loading it.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Calculate total elapsed seconds from time_segments; returns { seconds, isRunning }
function computeElapsed(segments) {
  if (!segments || segments.length === 0) return { seconds: 0, isRunning: false };
  let total = 0;
  let isRunning = false;
  for (const seg of segments) {
    const start = new Date(seg.start).getTime();
    const end = seg.end ? new Date(seg.end).getTime() : Date.now();
    total += Math.max(0, (end - start) / 1000);
    if (!seg.end) isRunning = true;
  }
  return { seconds: Math.floor(total), isRunning };
}

function formatDuration(totalSeconds) {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  return `${m}min ${String(s).padStart(2, '0')}s`;
}

function formatClock(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatDurationBetween(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  if (isNaN(s) || isNaN(e) || e < s) return null;
  const mins = Math.round((e - s) / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}min`;
}

// ============================================================
// LOGIN
// ============================================================
function LoginScreen({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLoggedIn(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans" data-testid="login-screen">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-8 text-center bg-indigo-600">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg overflow-hidden">
            <img src="/kasaalmar-logo.jpg" alt="Kasaalmar" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-2xl font-black text-white">Kasaalmar</h1>
          <p className="text-indigo-100 mt-1 text-sm">Panel de Gestión de Alojamientos</p>
        </div>
        <form onSubmit={submit} className="p-8 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Correo Electrónico</label>
            <div className="relative">
              <Mail className="w-5 h-5 text-slate-400 absolute left-3 top-2.5" />
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@ejemplo.com" className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña</label>
            <div className="relative">
              <LockKeyhole className="w-5 h-5 text-slate-400 absolute left-3 top-2.5" />
              <input data-testid="login-password-input" type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-11 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-indigo-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span></div>}
          <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-indigo-600 text-white font-bold py-2.5 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Iniciar Sesión
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// PHOTO GALLERY / UPLOADER
// ============================================================
function PhotosModal({ task, onClose, onChanged, currentUser }) {
  const [photos, setPhotos] = useState(task.photos || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [viewIdx, setViewIdx] = useState(null);

  const canEdit = currentUser.role === 'admin' || (currentUser.role === 'cleaner' && task.assigned_to_id === currentUser.id);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) { setError(`Máximo ${MAX_PHOTOS} fotos por tarea`); return; }
    setUploading(true);
    setError('');
    try {
      const dataUrl = await fileToCompressedDataUrl(file, 1024, 0.7);
      const { data } = await api.post(`/tasks/${task.id}/photos`, { data_url: dataUrl });
      const newPhoto = { data_url: dataUrl, uploaded_by: currentUser.name, uploaded_at: new Date().toISOString() };
      setPhotos([...photos, newPhoto]);
      onChanged?.(data.count);
    } catch (err) {
      // Prefer backend detail; fall back to the client-side compression error, then generic.
      const backendDetail = err.response?.data?.detail;
      const clientMessage = err?.message;
      const msg = backendDetail != null ? formatApiError(backendDetail) : (clientMessage || 'Error al subir foto');
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (idx) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      const { data } = await api.delete(`/tasks/${task.id}/photos/${idx}`);
      const next = photos.filter((_, i) => i !== idx);
      setPhotos(next);
      onChanged?.(data.count);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="photos-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-indigo-500" /> Fotos del trabajo</h3>
            <p className="text-xs text-slate-500 mt-0.5">{task.type} · {photos.length}/{MAX_PHOTOS} fotos</p>
          </div>
          <button onClick={onClose} data-testid="close-photos-modal" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {error && <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}

          {photos.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Todavía no hay fotos.</p>
              {canEdit && <p className="text-xs mt-1">Las fotos son opcionales — sirven como respaldo.</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((p, idx) => (
                <div key={`${p.uploaded_at || ''}-${idx}`} className="relative group aspect-square bg-slate-100 rounded-lg overflow-hidden">
                  <img src={p.data_url} alt={`foto-${idx}`} className="w-full h-full object-cover cursor-pointer" onClick={() => setViewIdx(idx)} data-testid={`photo-thumb-${idx}`} />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2 text-white text-[10px] leading-tight">
                    <div className="font-semibold truncate">{p.uploaded_by}</div>
                    <div className="opacity-70">{new Date(p.uploaded_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  {canEdit && (
                    <button onClick={() => removePhoto(idx)} data-testid={`delete-photo-${idx}`} className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 bg-rose-500 text-white p-1.5 rounded-md hover:bg-rose-600 transition-all">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center gap-3">
            <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${photos.length >= MAX_PHOTOS || uploading ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`} data-testid="upload-photo-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {uploading ? 'Subiendo...' : 'Tomar / Subir foto'}
              <input type="file" accept="image/*" capture="environment" onChange={handleFile} disabled={photos.length >= MAX_PHOTOS || uploading} className="hidden" data-testid="photo-file-input" />
            </label>
            <span className="text-xs text-slate-500">Se abre la cámara del móvil o galería. Máx {MAX_PHOTOS} fotos.</span>
          </div>
        )}
      </div>

      {viewIdx !== null && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setViewIdx(null)} data-testid="photo-lightbox">
          <img src={photos[viewIdx].data_url} alt="ampliada" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2" onClick={() => setViewIdx(null)}><X className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [owners, setOwners] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [checkinStaff, setCheckinStaff] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checkinTasks, setCheckinTasks] = useState([]);

  // Modals
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isCleanerModalOpen, setIsCleanerModalOpen] = useState(false);
  const [isCheckinStaffModalOpen, setIsCheckinStaffModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // { user, role, tone }
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [mergingProperty, setMergingProperty] = useState(null);
  const [propertyPhotosProperty, setPropertyPhotosProperty] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isCheckinTaskModalOpen, setIsCheckinTaskModalOpen] = useState(false);
  const [isEditBookingModalOpen, setIsEditBookingModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [viewingHistoryBooking, setViewingHistoryBooking] = useState(null);
  const [photosTask, setPhotosTask] = useState(null);
  const [editingObsId, setEditingObsId] = useState(null);
  const [obsDraft, setObsDraft] = useState('');
  const [savingObs, setSavingObs] = useState(false);
  const [editingExpId, setEditingExpId] = useState(null);
  const [expDraft, setExpDraft] = useState('');
  const [savingExp, setSavingExp] = useState(false);
  const [editingCiPriceId, setEditingCiPriceId] = useState(null);
  const [ciPriceDraft, setCiPriceDraft] = useState('');
  const [savingCiPrice, setSavingCiPrice] = useState(false);
  const [editingClPriceId, setEditingClPriceId] = useState(null);
  const [clPriceDraft, setClPriceDraft] = useState('');
  const [savingClPrice, setSavingClPrice] = useState(false);
  const [editingDate, setEditingDate] = useState({ id: null, field: null });
  const [dateDraft, setDateDraft] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingPricesId, setEditingPricesId] = useState(null);
  const [pricesDraft, setPricesDraft] = useState({ cleaning_price: '', checkin_price: '' });
  const [savingPrices, setSavingPrices] = useState(false);

  const [newBooking, setNewBooking] = useState({ property_id: '', guest: '', guest_phone: '', guest_email: '', guest_count: 0, check_in: '', check_out: '', observations: '', assigned_cleaner_id: '', assigned_cleaner_name: '', assigned_checkin_id: '', assigned_checkin_name: '' });
  const [newProperty, setNewProperty] = useState({ name: '', owner_id: '', owner_name: '', address: '', image: '', default_checkin_type: 'presencial', cleaning_price: '', checkin_price: '' });
  const [newOwner, setNewOwner] = useState({ name: '', email: '', phone: '' });
  const [newCleaner, setNewCleaner] = useState({ name: '', email: '', phone: '' });
  const [newCheckinStaff, setNewCheckinStaff] = useState({ name: '', email: '', phone: '' });
  const [newTask, setNewTask] = useState({ property_id: '', type: 'Limpieza General', date: '', assigned_to_id: '', assigned_to_name: '', notes: '' });
  const [newCheckinTask, setNewCheckinTask] = useState({ property_id: '', date: '', time: '', guest: '', assigned_to_id: '', assigned_to_name: '', type: 'presencial', notes: '' });

  const [bookingFilterDate, setBookingFilterDate] = useState('');
  const [bookingFilterMonth, setBookingFilterMonth] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [bookingFilterCleaner, setBookingFilterCleaner] = useState('');
  const [bookingFilterCheckin, setBookingFilterCheckin] = useState('');
  const [bookingFilterStatus, setBookingFilterStatus] = useState('');
  const [bookingSortDir, setBookingSortDir] = useState('asc');
  const [bookingFilterRange, setBookingFilterRange] = useState({ start: '', end: '', label: '' });
  const [unselectedProperties, setUnselectedProperties] = useState([]);
  const [showPropDropdown, setShowPropDropdown] = useState(false);
  const [propSearchQuery, setPropSearchQuery] = useState('');
  const [hidePastBookings, setHidePastBookings] = useState(true);
  const [propertiesCleaningFilter, setPropertiesCleaningFilter] = useState('');
  const [tasksStatusFilter, setTasksStatusFilter] = useState('');
  const [tasksFilterRange, setTasksFilterRange] = useState({ start: '', end: '', label: '' });
  const [tasksUnselectedProps, setTasksUnselectedProps] = useState([]);
  const [tasksShowPropDropdown, setTasksShowPropDropdown] = useState(false);
  const [receptionFilterRange, setReceptionFilterRange] = useState({ start: '', end: '', label: '' });
  const [receptionUnselectedProps, setReceptionUnselectedProps] = useState([]);
  const [receptionShowPropDropdown, setReceptionShowPropDropdown] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  const [globalError, setGlobalError] = useState('');
  const [taskFormError, setTaskFormError] = useState('');

  // ---------- INIT ----------
  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (stored && token) {
      api.get('/auth/me').then(({ data }) => {
        setCurrentUser(data);
      }).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }).finally(() => setInitializing(false));
    } else {
      setInitializing(false);
    }
  }, []);

  const reloadAll = useCallback(async () => {
    if (!currentUser) return;
    try {
      const [pRes, bRes, tRes, cRes, oRes, clRes, ciRes] = await Promise.all([
        api.get('/properties'),
        api.get('/bookings'),
        api.get('/tasks'),
        api.get('/checkin-tasks'),
        api.get('/owners'),
        api.get('/cleaners'),
        api.get('/checkin-staff'),
      ]);
      setProperties(pRes.data);
      setBookings(bRes.data);
      setTasks(tRes.data);
      setCheckinTasks(cRes.data);
      setOwners(oRes.data);
      setCleaners(clRes.data);
      setCheckinStaff(ciRes.data);
    } catch (err) {
      setGlobalError(formatApiError(err.response?.data?.detail));
    }
  }, [currentUser]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // ---------- AUTO-LOGOUT POR INACTIVIDAD (30 min) ----------
  useEffect(() => {
    if (!currentUser) return undefined;
    const INACTIVITY_MS = 30 * 60 * 1000;
    let timerId;
    const doLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setCurrentUser(null);
      setCurrentTab('dashboard');
      try {
        window.alert('Tu sesión se ha cerrado por inactividad. Por favor, vuelve a iniciar sesión.');
      } catch (err) { console.warn('alert() no disponible al cerrar sesión por inactividad:', err); }
    };
    const reset = () => {
      clearTimeout(timerId);
      timerId = setTimeout(doLogout, INACTIVITY_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timerId);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [currentUser]);

  if (initializing) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  }

  if (!currentUser) {
    return <LoginScreen onLoggedIn={setCurrentUser} />;
  }

  // ---------- ROLES ----------
  const isAdmin = currentUser.role === 'admin';
  const isCleaner = currentUser.role === 'cleaner';
  const isCheckinRole = currentUser.role === 'checkin';
  const isOwner = currentUser.role === 'owner';

  const visibleProperties = properties;
  const visibleBookings = bookings;
  const visibleTasks = tasks;
  const visibleCheckinTasks = checkinTasks;

  const todaysCheckIns = visibleBookings.filter(b => b.check_in === todayStr && b.status !== 'cancelled');
  const todaysCheckOuts = visibleBookings.filter(b => b.check_out === todayStr && b.status !== 'cancelled');
  const todaysTasks = visibleTasks.filter(t => t.date === todayStr);
  const todaysCheckinTasks = visibleCheckinTasks.filter(t => t.date === todayStr);
  const pendingTasksCount = visibleTasks.filter(t => t.status !== 'completed').length;
  const pendingCheckinsCount = visibleCheckinTasks.filter(t => t.status !== 'completed').length;
  const propertiesNeedingCleaning = visibleProperties.filter(p => p.cleaning_status === 'dirty').length;

  const getProperty = (id) => properties.find(p => p.id === id);

  // ---------- ACTIONS ----------
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setCurrentTab('dashboard');
  };

  const wrap = async (fn) => {
    try { await fn(); await reloadAll(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || 'Error'); }
  };

  const handleTaskStatusChange = async (task, newStatus) => {
    setTaskFormError('');
    try {
      await api.patch(`/tasks/${task.id}`, { status: newStatus });
      await reloadAll();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      alert(msg);
      if (msg.includes('foto')) setPhotosTask(task);
    }
  };

  const handleTaskStart = (task) => wrap(() => api.post(`/tasks/${task.id}/start`));
  const handleTaskPause = (task) => wrap(() => api.post(`/tasks/${task.id}/pause`));
  const handleTaskFinish = async (task) => {
    try {
      await api.post(`/tasks/${task.id}/finish`);
      await reloadAll();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      alert(msg);
      if (msg.toLowerCase().includes('foto')) setPhotosTask(task);
    }
  };

  const handleCheckinStatusChange = (id, status) => wrap(() => api.patch(`/checkin-tasks/${id}`, { status }));
  const handleTaskNotesChange = (id, notes) => api.patch(`/tasks/${id}`, { notes }).catch(() => {});

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`¿Eliminar esta tarea de limpieza?\n\nPropiedad: ${getProperty(task.property_id)?.name || '?'}\nFecha: ${task.date}\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleDeleteCheckinTask = async (task) => {
    if (!window.confirm(`¿Eliminar esta tarea de check-in?\n\nPropiedad: ${getProperty(task.property_id)?.name || '?'}\nFecha: ${task.date}\nHuésped: ${task.guest || '—'}\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/checkin-tasks/${task.id}`);
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  const handleCheckinNotesChange = (id, notes) => api.patch(`/checkin-tasks/${id}`, { notes }).catch(() => {});

  const handleCheckinArrival = async (task, arrived) => {
    const stamp = new Date().toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
    const prefix = arrived ? `✅ LLEGÓ ${stamp}` : `❌ NO SE PRESENTÓ ${stamp}`;
    const existing = task.notes || '';
    const newNotes = existing ? `${prefix}\n${existing}` : prefix;
    try {
      await api.patch(`/checkin-tasks/${task.id}`, { status: 'completed', notes: newNotes });
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleAddBooking = (e) => { e.preventDefault(); wrap(async () => { const cleaner = cleaners.find(c => c.id === newBooking.assigned_cleaner_id); const isAutoCheckin = newBooking.assigned_checkin_id === '__auto__'; const checkin = isAutoCheckin ? null : checkinStaff.find(c => c.id === newBooking.assigned_checkin_id); const created = await api.post('/bookings', { ...newBooking, guest_count: parseInt(newBooking.guest_count) || 0, assigned_cleaner_name: cleaner?.name || '', assigned_checkin_id: isAutoCheckin ? '' : newBooking.assigned_checkin_id, assigned_checkin_name: isAutoCheckin ? 'Automático' : (checkin?.name || '') }); if (isAutoCheckin && created?.data?.id) { try { const tasks = await api.get('/checkin-tasks'); const t = tasks.data.find(x => x.booking_id === created.data.id); if (t) await api.patch(`/checkin-tasks/${t.id}`, { type: 'automatico' }); } catch (_) { /* opcional */ } } setIsBookingModalOpen(false); setNewBooking({ property_id: '', guest: '', guest_phone: '', guest_email: '', guest_count: 0, check_in: '', check_out: '', observations: '', assigned_cleaner_id: '', assigned_cleaner_name: '', assigned_checkin_id: '', assigned_checkin_name: '' }); }); };
  const handleEditBooking = (e) => { e.preventDefault(); wrap(async () => { const cleaner = cleaners.find(c => c.id === editingBooking.assigned_cleaner_id); await api.patch(`/bookings/${editingBooking.id}`, { guest: editingBooking.guest, guest_phone: editingBooking.guest_phone || '', guest_email: editingBooking.guest_email || '', guest_count: parseInt(editingBooking.guest_count) || 0, check_in: editingBooking.check_in, check_out: editingBooking.check_out, observations: editingBooking.observations || '', extra_expenses: parseFloat(editingBooking.extra_expenses) || 0, admin_notes: editingBooking.admin_notes || '', assigned_cleaner_id: editingBooking.assigned_cleaner_id || '', assigned_cleaner_name: cleaner?.name || '', assigned_checkin_id: editingBooking.assigned_checkin_id || '', assigned_checkin_name: editingBooking.assigned_checkin_name || '' }); setIsEditBookingModalOpen(false); setEditingBooking(null); }); };

  const handleAssignCleaner = async (booking, cleanerId) => {
    const cleaner = cleaners.find(c => c.id === cleanerId);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: cleanerId || '',
        assigned_cleaner_name: cleaner?.name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleAssignCheckin = async (booking, staffId) => {
    const isAuto = staffId === '__auto__';
    const staff = isAuto ? null : checkinStaff.find(c => c.id === staffId);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: isAuto ? '' : (staffId || ''),
        assigned_checkin_name: isAuto ? 'Automático' : (staff?.name || ''),
      });
      // Si se elige Automático, marcamos el check-in task asociado como self-checkin
      if (isAuto) {
        const task = visibleCheckinTasks.find(x => x.booking_id === booking.id);
        if (task) {
          try { await api.patch(`/checkin-tasks/${task.id}`, { type: 'automatico' }); } catch (_) { /* opcional */ }
        }
      }
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const openObsEditor = (b) => { setEditingObsId(b.id); setObsDraft(b.observations || ''); };
  const cancelObsEditor = () => { setEditingObsId(null); setObsDraft(''); };
  const saveObservations = async (booking) => {
    setSavingObs(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: obsDraft,
        extra_expenses: booking.extra_expenses ?? 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      setEditingObsId(null); setObsDraft('');
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingObs(false); }
  };

  const openDateEditor = (b, field) => { setEditingDate({ id: b.id, field }); setDateDraft(field === 'in' ? b.check_in : b.check_out); };
  const cancelDateEditor = () => { setEditingDate({ id: null, field: null }); setDateDraft(''); };
  const saveDate = async (booking) => {
    if (!dateDraft) return;
    const field = editingDate.field;
    const newCheckIn = field === 'in' ? dateDraft : booking.check_in;
    const newCheckOut = field === 'out' ? dateDraft : booking.check_out;
    if (newCheckOut < newCheckIn) {
      alert('La fecha de salida no puede ser anterior a la fecha de entrada.');
      return;
    }
    setSavingDate(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: newCheckIn,
        check_out: newCheckOut,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      cancelDateEditor();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingDate(false); }
  };

  const openExpEditor = (b) => { setEditingExpId(b.id); setExpDraft(b.extra_expenses ?? 0); };
  const cancelExpEditor = () => { setEditingExpId(null); setExpDraft(''); };

  const getBookingCheckinPrice = (b) => {
    if (b.checkin_price !== null && b.checkin_price !== undefined) return Number(b.checkin_price) || 0;
    const prop = properties.find(p => p.id === b.property_id);
    return Number(prop?.checkin_price) || 0;
  };
  const getBookingCleaningPrice = (b) => {
    if (b.cleaning_price !== null && b.cleaning_price !== undefined) return Number(b.cleaning_price) || 0;
    const prop = properties.find(p => p.id === b.property_id);
    return Number(prop?.cleaning_price) || 0;
  };
  const getBookingTotal = (b) => getBookingCleaningPrice(b) + getBookingCheckinPrice(b) + (Number(b.extra_expenses) || 0);
  const openClPriceEditor = (b) => { setEditingClPriceId(b.id); setClPriceDraft(getBookingCleaningPrice(b)); };
  const cancelClPriceEditor = () => { setEditingClPriceId(null); setClPriceDraft(''); };
  const saveCleaningPrice = async (booking) => {
    setSavingClPrice(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        cleaning_price: parseFloat(clPriceDraft) || 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      setEditingClPriceId(null); setClPriceDraft('');
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingClPrice(false); }
  };
  const openCiPriceEditor = (b) => { setEditingCiPriceId(b.id); setCiPriceDraft(getBookingCheckinPrice(b)); };
  const cancelCiPriceEditor = () => { setEditingCiPriceId(null); setCiPriceDraft(''); };
  const saveCheckinPrice = async (booking) => {
    setSavingCiPrice(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        checkin_price: parseFloat(ciPriceDraft) || 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      setEditingCiPriceId(null); setCiPriceDraft('');
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCiPrice(false); }
  };

  const saveExpenses = async (booking) => {
    setSavingExp(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: parseFloat(expDraft) || 0,
        admin_notes: booking.admin_notes || '',
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      setEditingExpId(null); setExpDraft('');
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingExp(false); }
  };

  const openNotesEditor = (b) => { setEditingNotesId(b.id); setNotesDraft(b.admin_notes || ''); };
  const cancelNotesEditor = () => { setEditingNotesId(null); setNotesDraft(''); };
  const saveAdminNotes = async (booking) => {
    setSavingNotes(true);
    try {
      await api.patch(`/bookings/${booking.id}`, {
        guest: booking.guest,
        guest_phone: booking.guest_phone || '',
        guest_email: booking.guest_email || '',
        guest_count: booking.guest_count ?? 0,
        check_in: booking.check_in,
        check_out: booking.check_out,
        observations: booking.observations || '',
        extra_expenses: booking.extra_expenses ?? 0,
        admin_notes: notesDraft,
        assigned_cleaner_id: booking.assigned_cleaner_id || '',
        assigned_cleaner_name: booking.assigned_cleaner_name || '',
        assigned_checkin_id: booking.assigned_checkin_id || '',
        assigned_checkin_name: booking.assigned_checkin_name || '',
      });
      await reloadAll();
      setEditingNotesId(null); setNotesDraft('');
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingNotes(false); }
  };
  const handleCancelBooking = (id) => { if (!window.confirm('¿Cancelar esta reserva?')) return; wrap(() => api.post(`/bookings/${id}/cancel`)); };
  const handleChangeBookingStatus = async (booking, newStatus) => {
    if (newStatus === 'cancelled') {
      if (!window.confirm('¿Marcar esta reserva como Cancelada?')) return;
      try { await api.post(`/bookings/${booking.id}/cancel`); await reloadAll(); }
      catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    } else {
      try { await api.post(`/bookings/${booking.id}/confirm`); await reloadAll(); }
      catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    }
  };

  const handleAddProperty = (e) => { e.preventDefault(); const owner = owners.find(o => o.id === newProperty.owner_id); const payload = { ...newProperty, owner_name: owner?.name || newProperty.owner_name, cleaning_price: parseFloat(newProperty.cleaning_price) || 0, checkin_price: parseFloat(newProperty.checkin_price) || 0 }; wrap(async () => { if (editingProperty) await api.patch(`/properties/${editingProperty.id}`, payload); else await api.post('/properties', payload); setIsPropertyModalOpen(false); setEditingProperty(null); setNewProperty({ name: '', owner_id: '', owner_name: '', address: '', image: '', default_checkin_type: 'presencial', cleaning_price: '', checkin_price: '' }); }); };

  const openEditProperty = (p) => {
    setEditingProperty(p);
    setNewProperty({
      name: p.name || '', owner_id: p.owner_id || '', owner_name: p.owner_name || '', address: p.address || '',
      image: p.image || '', default_checkin_type: p.default_checkin_type || 'presencial',
      cleaning_price: p.cleaning_price ?? 0, checkin_price: p.checkin_price ?? 0,
    });
    setIsPropertyModalOpen(true);
  };

  const openPricesEditor = (p) => {
    setEditingPricesId(p.id);
    setPricesDraft({ cleaning_price: p.cleaning_price ?? 0, checkin_price: p.checkin_price ?? 0 });
  };
  const cancelPricesEditor = () => { setEditingPricesId(null); setPricesDraft({ cleaning_price: '', checkin_price: '' }); };
  const savePrices = async (p) => {
    setSavingPrices(true);
    try {
      await api.patch(`/properties/${p.id}`, {
        name: p.name, address: p.address || '', owner_id: p.owner_id || null, owner_name: p.owner_name || '',
        default_checkin_type: p.default_checkin_type || 'presencial',
        cleaning_price: parseFloat(pricesDraft.cleaning_price) || 0,
        checkin_price: parseFloat(pricesDraft.checkin_price) || 0,
        image: p.image || '', cleaning_status: p.cleaning_status || 'clean',
      });
      await reloadAll();
      setEditingPricesId(null); setPricesDraft({ cleaning_price: '', checkin_price: '' });
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPrices(false); }
  };

  const handleChangePropertyOwner = async (p, newOwnerId) => {
    const owner = owners.find(o => o.id === newOwnerId);
    try {
      await api.patch(`/properties/${p.id}`, {
        name: p.name, address: p.address || '',
        owner_id: newOwnerId || null,
        owner_name: owner?.name || '',
        default_checkin_type: p.default_checkin_type || 'presencial',
        cleaning_price: p.cleaning_price ?? 0,
        checkin_price: p.checkin_price ?? 0,
        image: p.image || '', cleaning_status: p.cleaning_status || 'clean',
      });
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleAddOwner = (e) => { e.preventDefault(); wrap(async () => { await api.post('/owners', newOwner); setIsOwnerModalOpen(false); setNewOwner({ name: '', email: '', phone: '' }); }); };
  const handleAddCleaner = (e) => { e.preventDefault(); wrap(async () => { await api.post('/cleaners', newCleaner); setIsCleanerModalOpen(false); setNewCleaner({ name: '', email: '', phone: '' }); }); };
  const handleAddCheckinStaff = (e) => { e.preventDefault(); wrap(async () => { await api.post('/checkin-staff', newCheckinStaff); setIsCheckinStaffModalOpen(false); setNewCheckinStaff({ name: '', email: '', phone: '' }); }); };

  const handleSaveEditUser = async (updatedFields) => {
    if (!editingUser) return;
    try {
      await api.patch(`/users/${editingUser.user.id}`, updatedFields);
      setEditingUser(null);
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  const handleDeleteUser = async () => {
    if (!editingUser) return;
    if (!window.confirm(`¿Eliminar a "${editingUser.user.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/users/${editingUser.user.id}`);
      setEditingUser(null);
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const handleAddTask = (e) => { e.preventDefault(); const cl = cleaners.find(c => c.id === newTask.assigned_to_id); const payload = { ...newTask, assigned_to_name: cl?.name || '' }; wrap(async () => { await api.post('/tasks', payload); setIsTaskModalOpen(false); setNewTask({ property_id: '', type: 'Limpieza General', date: '', assigned_to_id: '', assigned_to_name: '', notes: '' }); }); };

  const handleSyncCleaners = async () => {
    try {
      const r = await api.post('/tasks/sync-from-bookings');
      alert(`✅ ${r.data.updated} tarea(s) de limpieza actualizada(s) desde reservas.`);
      await reloadAll();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  const handleAddCheckinTask = (e) => { e.preventDefault(); const person = newCheckinTask.type === 'automatico' ? { name: 'Caja de Seguridad', id: '' } : (checkinStaff.find(c => c.id === newCheckinTask.assigned_to_id) || { name: '', id: '' }); const payload = { ...newCheckinTask, assigned_to_name: person.name }; wrap(async () => { await api.post('/checkin-tasks', payload); setIsCheckinTaskModalOpen(false); setNewCheckinTask({ property_id: '', date: '', time: '', guest: '', assigned_to_id: '', assigned_to_name: '', type: 'presencial', notes: '' }); }); };

  // ============================================================
  // VIEWS
  // ============================================================
  const goToBookingsForDate = (dateStr) => {
    setBookingFilterDate(dateStr);
    setBookingFilterMonth('');
    setBookingSearch(''); setBookingSearchInput('');
    setBookingFilterCleaner(''); setBookingFilterCheckin('');
    setBookingFilterStatus('active');
    setUnselectedProperties([]);
    setHidePastBookings(false);
    setCurrentTab('bookings');
  };
  const goToCheckinsPending = () => { setCurrentTab('checkins'); };
  const goToTasksPending = () => { setCurrentTab('tasks'); };
  const goToPropertiesDirty = () => { setPropertiesCleaningFilter('dirty'); setCurrentTab('properties'); };

  const DashboardView = () => (
    <div className="space-y-6" data-testid="dashboard-view">
      {todaysCheckOuts.length > 0 && (
        <div data-testid="departure-alert" className="bg-gradient-to-r from-orange-50 to-rose-50 border-l-4 border-orange-400 rounded-xl p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="shrink-0 p-2 bg-orange-100 rounded-full">
              <LogOut className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h3 className="text-sm font-bold text-orange-900 uppercase tracking-wide">Aviso de Salida · Preparar Limpieza</h3>
                <span className="text-xs bg-orange-200 text-orange-900 px-2 py-0.5 rounded-full font-semibold">{todaysCheckOuts.length}</span>
              </div>
              <p className="text-xs text-orange-800/80 mb-3">Hoy estas propiedades pasan de <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 bg-indigo-100 rounded"><User className="w-3 h-3 text-indigo-700" /> Estancia</span> a <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 bg-rose-100 rounded"><Trash2 className="w-3 h-3 text-rose-700" /> Sucia</span> — hay que limpiarlas.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {todaysCheckOuts.map(b => {
                  const p = getProperty(b.property_id);
                  const cleaningTask = visibleTasks.find(t => t.property_id === b.property_id && t.date === b.check_out);
                  const cleaner = cleaningTask?.assigned_to_name || b.assigned_cleaner_name;
                  return (
                    <div key={b.id} data-testid={`departure-${b.id}`} className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-semibold text-sm text-slate-800 truncate">{p?.name || 'Sin propiedad'}</p>
                        {cleaningTask && (cleaningTask.status === 'completed' ? <span title="Limpieza completada" className="text-emerald-600"><CheckCircle2 className="w-4 h-4" /></span> : cleaningTask.status === 'in-progress' ? <span title="En curso" className="text-amber-600"><Clock className="w-4 h-4" /></span> : <span title="Pendiente" className="text-slate-400"><Clock className="w-4 h-4" /></span>)}
                      </div>
                      <p className="text-xs text-slate-500 truncate">Sale: {b.guest}</p>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3 text-rose-500" /> {cleaner || <span className="italic text-slate-400">Sin limpiadora</span>}</p>
                    </div>
                  );
                })}
              </div>
              <button onClick={goToTasksPending} className="mt-3 text-xs font-semibold text-orange-800 hover:text-orange-900 underline">Ver todas las limpiezas →</button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={ArrowRightLeft} label="Entradas" value={todaysCheckIns.length} tone="blue" cta="Ver entradas" onClick={() => goToBookingsForDate(todayStr)} />
        <StatCard icon={LogOut} label="Salidas" value={todaysCheckOuts.length} tone="orange" cta="Ver salidas" onClick={() => goToBookingsForDate(todayStr)} />
        <StatCard icon={Key} label="Check-ins Ptes." value={pendingCheckinsCount} tone="purple" cta="Ver check-ins" onClick={goToCheckinsPending} />
        <StatCard icon={Sparkles} label="Limpiezas Ptes." value={pendingTasksCount} tone="rose" cta="Ver limpiezas" onClick={goToTasksPending} />
        <StatCard icon={AlertCircle} label="Aptos. Sucios" value={propertiesNeedingCleaning} tone="emerald" cta="Ver propiedades" onClick={goToPropertiesDirty} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Movimientos" icon={<CalendarDays className="w-5 h-5 text-indigo-500" />} right={<span className="text-sm text-slate-500">{formatDisplayDate(todayStr)}</span>}>
          {todaysCheckOuts.length === 0 && todaysCheckIns.length === 0 ? <p className="p-6 text-center text-slate-500">No hay movimientos.</p> : (
            <>
              {todaysCheckOuts.map(b => { const p = getProperty(b.property_id); return <div key={b.id} className="p-4 hover:bg-slate-50 flex items-center gap-3"><div className="w-2 h-10 bg-orange-400 rounded-full"></div><div><p className="font-medium text-slate-800">Salida: {b.guest}</p><p className="text-sm text-slate-500">{p?.name}</p></div></div>; })}
              {todaysCheckIns.map(b => { const p = getProperty(b.property_id); return <div key={b.id} className="p-4 hover:bg-slate-50 flex items-center gap-3"><div className="w-2 h-10 bg-blue-400 rounded-full"></div><div><p className="font-medium text-slate-800">Entrada: {b.guest}</p><p className="text-sm text-slate-500">{p?.name}</p></div></div>; })}
            </>
          )}
        </Panel>

        <Panel title="Check-ins" icon={<Key className="w-5 h-5 text-purple-500" />}>
          {todaysCheckinTasks.length === 0 ? <p className="p-6 text-center text-slate-500">No hay entregas programadas.</p> : todaysCheckinTasks.map(t => { const p = getProperty(t.property_id); return (
            <div key={t.id} className="p-4 hover:bg-slate-50 flex items-center gap-3">
              {t.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : t.status === 'in-progress' ? <Clock className="w-5 h-5 text-amber-500" /> : <div className="w-5 h-5 rounded-full border-2 border-slate-300" />}
              <div><p className="font-medium text-slate-800 flex items-center gap-1.5">{t.time} - {t.guest} {t.type === 'automatico' ? <Lock className="w-3.5 h-3.5 text-slate-400" /> : <User className="w-3.5 h-3.5 text-slate-400" />}</p><p className="text-sm text-slate-500">{p?.name} · {t.assigned_to_name}</p></div>
            </div>
          ); })}
        </Panel>

        <Panel title="Limpieza" icon={<Sparkles className="w-5 h-5 text-rose-500" />}>
          {todaysTasks.length === 0 ? <p className="p-6 text-center text-slate-500">No hay limpiezas hoy.</p> : todaysTasks.map(t => { const p = getProperty(t.property_id); return (
            <div key={t.id} className="p-4 hover:bg-slate-50 flex items-center gap-3">
              {t.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : t.status === 'in-progress' ? <Clock className="w-5 h-5 text-amber-500" /> : <div className="w-5 h-5 rounded-full border-2 border-slate-300" />}
              <div><p className="font-medium text-slate-800">{t.type}</p><p className="text-sm text-slate-500">{p?.name} · {t.assigned_to_name}</p></div>
            </div>
          ); })}
        </Panel>
      </div>
    </div>
  );

  const PropertiesView = () => {
    const filteredProperties = (() => {
      if (!propertiesCleaningFilter) return visibleProperties;
      if (propertiesCleaningFilter === 'occupied') {
        return visibleProperties.filter(p => visibleBookings.some(b => b.property_id === p.id && b.status !== 'cancelled' && (b.check_in || '') <= todayStr && (b.check_out || '') > todayStr));
      }
      // Excluir de los estados de limpieza las propiedades actualmente ocupadas
      return visibleProperties.filter(p => {
        const occupied = visibleBookings.some(b => b.property_id === p.id && b.status !== 'cancelled' && (b.check_in || '') <= todayStr && (b.check_out || '') > todayStr);
        if (occupied) return false;
        return (p.cleaning_status || 'clean') === propertiesCleaningFilter;
      });
    })();
    return (
    <div className="space-y-6" data-testid="properties-view">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-slate-800">{isAdmin ? 'Todas las Propiedades' : 'Mis Propiedades'} <span className="text-sm font-normal text-slate-500 ml-2">({filteredProperties.length}{propertiesCleaningFilter ? ` / ${visibleProperties.length}` : ''})</span></h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Estado:</label>
          <select data-testid="properties-cleaning-filter" value={propertiesCleaningFilter} onChange={(e) => setPropertiesCleaningFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
            <option value="">Todas</option>
            <option value="occupied">🛏 Estancia</option>
            <option value="clean">✅ Limpia</option>
            <option value="dirty">🗑 Sucia</option>
            <option value="in-progress">⏱ Limpiando</option>
          </select>
          {propertiesCleaningFilter && <button data-testid="properties-clear-filter" onClick={() => setPropertiesCleaningFilter('')} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg flex items-center gap-1"><X className="w-3 h-3" /> Limpiar</button>}
          {isAdmin && <button data-testid="new-property-button" onClick={() => { setEditingProperty(null); setNewProperty({ name: '', owner_id: '', owner_name: '', address: '', image: '', default_checkin_type: 'presencial', cleaning_price: '', checkin_price: '' }); setIsPropertyModalOpen(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva Propiedad</button>}
        </div>
      </div>
      {filteredProperties.length === 0 && propertiesCleaningFilter && (
        <div className="bg-white rounded-xl border border-slate-100 p-8 text-center text-slate-500">
          <Sparkles className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="font-medium">¡No hay propiedades en este estado!</p>
          <button onClick={() => setPropertiesCleaningFilter('')} className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 font-medium">Ver todas las propiedades</button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProperties.map(p => (
          <div key={p.id} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex flex-col" data-testid={`property-card-${p.id}`}>
            <div className="h-40 relative">
              <PropertySlideshow photos={p.photos} fallback={p.image} name={p.name} />
              <div className="absolute top-3 right-3">
                {(() => {
                  const isOccupied = visibleBookings.some(b => b.property_id === p.id && b.status !== 'cancelled' && (b.check_in || '') <= todayStr && (b.check_out || '') > todayStr);
                  if (isOccupied) return <span data-testid={`prop-badge-${p.id}`} className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1"><User className="w-3 h-3" /> Estancia</span>;
                  if (p.cleaning_status === 'in-progress') return <span data-testid={`prop-badge-${p.id}`} className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1"><Clock className="w-3 h-3" /> Limpiando</span>;
                  if (p.cleaning_status === 'dirty') return <span data-testid={`prop-badge-${p.id}`} className="bg-rose-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1"><Trash2 className="w-3 h-3" /> Sucia</span>;
                  return <span data-testid={`prop-badge-${p.id}`} className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Limpia</span>;
                })()}
              </div>
              {isAdmin && <button data-testid={`property-photos-${p.id}`} onClick={() => setPropertyPhotosProperty(p)} className="absolute bottom-3 right-3 bg-white/90 hover:bg-white text-slate-800 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {(p.photos || []).length || 0} fotos</button>}
            </div>
            <div className="p-5 flex flex-col flex-grow">
              <h3 className="text-lg font-bold text-slate-800 mb-1">{p.name}</h3>
              <p className="text-sm text-slate-500 flex items-center gap-1 mb-3"><MapPin className="w-4 h-4" /> {p.address || <span className="italic text-slate-400">Sin dirección</span>}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded-md flex items-center gap-1.5">{p.default_checkin_type === 'automatico' ? <><Lock className="w-3.5 h-3.5" /> Self Checkin</> : <><User className="w-3.5 h-3.5" /> Presencial</>}</span>
                {isAdmin && (editingPricesId === p.id ? (
                  <div className="flex items-center gap-1.5 flex-wrap w-full" data-testid={`prices-editor-${p.id}`}>
                    <label className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Limp.
                      <input type="number" min="0" step="0.01" autoFocus data-testid={`price-clean-input-${p.id}`} value={pricesDraft.cleaning_price} onChange={(e) => setPricesDraft({ ...pricesDraft, cleaning_price: e.target.value })} className="w-16 ml-1 border border-emerald-300 rounded px-1.5 py-0.5 text-xs outline-none focus:ring-1 focus:ring-emerald-500 bg-white" />€
                    </label>
                    <label className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded-md flex items-center gap-1.5"><Key className="w-3.5 h-3.5" /> C-in
                      <input type="number" min="0" step="0.01" data-testid={`price-checkin-input-${p.id}`} value={pricesDraft.checkin_price} onChange={(e) => setPricesDraft({ ...pricesDraft, checkin_price: e.target.value })} className="w-16 ml-1 border border-purple-300 rounded px-1.5 py-0.5 text-xs outline-none focus:ring-1 focus:ring-purple-500 bg-white" />€
                    </label>
                    <button data-testid={`prices-save-${p.id}`} onClick={() => savePrices(p)} disabled={savingPrices} className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-2 py-1 rounded-md flex items-center gap-1 disabled:opacity-60">
                      {savingPrices ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    </button>
                    <button data-testid={`prices-cancel-${p.id}`} onClick={cancelPricesEditor} disabled={savingPrices} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium px-2 py-1 rounded-md flex items-center gap-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button data-testid={`prices-edit-${p.id}`} onClick={() => openPricesEditor(p)} title="Click para editar tarifas" className="flex flex-wrap gap-2 items-center hover:opacity-80 transition">
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-md flex items-center gap-1.5 border border-transparent hover:border-emerald-200"><Sparkles className="w-3.5 h-3.5" /> Limpieza: {p.cleaning_price}€</span>
                    <span className="text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-md flex items-center gap-1.5 border border-transparent hover:border-purple-200"><Key className="w-3.5 h-3.5" /> Check-in: {p.checkin_price}€</span>
                    <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md flex items-center gap-1.5 border border-indigo-200" title="Precio total del servicio (Limpieza + Check-in)"><Briefcase className="w-3.5 h-3.5" /> Total servicio: {((Number(p.cleaning_price) || 0) + (Number(p.checkin_price) || 0)).toFixed(2)}€</span>
                  </button>
                ))}
              </div>
              <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs shrink-0">{(p.owner_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('')}</div>
                  {isAdmin ? (
                    <select
                      data-testid={`property-owner-${p.id}`}
                      value={p.owner_id || ''}
                      onChange={(e) => handleChangePropertyOwner(p, e.target.value)}
                      className="text-sm font-medium text-slate-700 bg-transparent border-0 focus:ring-2 focus:ring-indigo-300 rounded px-1 py-0.5 outline-none cursor-pointer min-w-0 flex-1 truncate hover:bg-indigo-50"
                      title={p.owner_name || 'Sin propietario'}
                    >
                      <option value="">Sin propietario</option>
                      {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  ) : (
                    <span className="text-sm font-medium text-slate-700 truncate max-w-[140px]" title={p.owner_name}>{p.owner_name || <span className="italic text-slate-400">Sin propietario</span>}</span>
                  )}
                </div>
                {isAdmin && <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`merge-property-${p.id}`} onClick={() => setMergingProperty(p)} className="text-xs text-amber-700 hover:text-amber-900 font-medium bg-amber-50 hover:bg-amber-100 px-2.5 py-1.5 rounded-lg" title="Fusionar con otra propiedad">Fusionar</button>
                  <button data-testid={`edit-property-${p.id}`} onClick={() => openEditProperty(p)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg">Editar</button>
                </div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  const BookingsView = () => {
    const filteredBookings = useMemo(() => visibleBookings.filter(b => {
      if (unselectedProperties.includes(b.property_id)) return false;
      if (hidePastBookings && b.status === 'past') return false;
      if (bookingFilterDate && (b.check_in > bookingFilterDate || b.check_out < bookingFilterDate)) return false;
      if (bookingFilterMonth) {
        const im = b.check_in.substring(0, 7), om = b.check_out.substring(0, 7);
        if (bookingFilterMonth < im || bookingFilterMonth > om) return false;
      }
      if (bookingFilterCleaner === '__none__') { if (b.assigned_cleaner_id) return false; }
      else if (bookingFilterCleaner && b.assigned_cleaner_id !== bookingFilterCleaner) return false;
      if (bookingFilterCheckin === '__none__') { if (b.assigned_checkin_id) return false; }
      else if (bookingFilterCheckin && b.assigned_checkin_id !== bookingFilterCheckin) return false;
      if (bookingFilterStatus === 'cancelled' && b.status !== 'cancelled') return false;
      if (bookingFilterStatus === 'active' && b.status === 'cancelled') return false;
      if (bookingFilterRange.start && bookingFilterRange.end) {
        // Include if booking overlaps the range
        if ((b.check_in || '') > bookingFilterRange.end) return false;
        if ((b.check_out || '') < bookingFilterRange.start) return false;
      }
      if (bookingSearch) {
        const q = bookingSearch.toLowerCase().trim();
        const hay = `${b.guest || ''} ${b.guest_phone || ''} ${b.guest_email || ''} ${b.observations || ''} ${b.admin_notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      // Ordena las reservas cuya salida ya pasó al final; el resto por fecha de entrada según dirección elegida.
      const aPast = (a.check_out || '') < todayStr;
      const bPast = (b.check_out || '') < todayStr;
      if (aPast !== bPast) return aPast ? 1 : -1;
      const cmp = (a.check_in || '').localeCompare(b.check_in || '');
      return bookingSortDir === 'desc' ? -cmp : cmp;
    }), [visibleBookings, unselectedProperties, hidePastBookings, bookingFilterDate, bookingFilterMonth, bookingFilterCleaner, bookingFilterCheckin, bookingFilterStatus, bookingFilterRange, bookingSearch, bookingSortDir]);
    const toggleProp = (id) => setUnselectedProperties(unselectedProperties.includes(id) ? unselectedProperties.filter(x => x !== id) : [...unselectedProperties, id]);
    const anyFilterActive = bookingFilterDate || bookingFilterMonth || unselectedProperties.length > 0 || bookingSearch || bookingFilterCleaner || bookingFilterCheckin || bookingFilterStatus || bookingFilterRange.start || !hidePastBookings;
    const clearAll = () => { setBookingFilterDate(''); setBookingFilterMonth(''); setUnselectedProperties([]); setBookingSearch(''); setBookingSearchInput(''); setBookingFilterCleaner(''); setBookingFilterCheckin(''); setBookingFilterStatus(''); setBookingFilterRange({ start: '', end: '', label: '' }); };
    const showAll = () => { clearAll(); setHidePastBookings(false); };
    const applySearch = () => setBookingSearch(bookingSearchInput.trim());

    const exportBookingsCSV = () => {
      if (!filteredBookings.length) return;
      const statusLabel = (s) => s === 'cancelled' ? 'Cancelada' : s === 'past' ? 'Pasada' : s === 'arriving' ? 'Entrando' : s === 'departing' ? 'Saliendo' : 'Próxima';
      const headers = ['Huésped', 'Teléfono', 'Email', 'Nº personas', 'Propiedad', 'Entrada', 'Salida', 'Estado', 'Limpiadora', 'Recepcionista', 'Observaciones', 'Precio Servicio (€)', 'Gastos Extras (€)', 'Check-in (€)', 'Limpieza (€)', 'Total (€)', 'Notas Admin'];
      const rows = filteredBookings.map(b => {
        const p = getProperty(b.property_id);
        return [
          b.guest || '', b.guest_phone || '', b.guest_email || '', b.guest_count ?? 0,
          p?.name || '', b.check_in || '', b.check_out || '', statusLabel(b.status),
          b.assigned_cleaner_name || '', b.assigned_checkin_name || '',
          (b.observations || '').replace(/\r?\n/g, ' '),
          (getBookingCleaningPrice(b) + getBookingCheckinPrice(b)).toFixed(2),
          (b.extra_expenses ?? 0).toFixed(2),
          getBookingCheckinPrice(b).toFixed(2),
          getBookingCleaningPrice(b).toFixed(2),
          getBookingTotal(b).toFixed(2),
          (b.admin_notes || '').replace(/\r?\n/g, ' '),
        ];
      });
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kasaalmar-reservas-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="bookings-view">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center flex-wrap gap-3">
          <h2 className="text-xl font-bold text-slate-800">{isAdmin ? 'Todas las Reservas' : 'Reservas de Mis Alojamientos'} <span className="text-sm font-normal text-slate-500 ml-2">({filteredBookings.length})</span></h2>
          <div className="flex items-center gap-2">
            {(isAdmin || isOwner) && <button data-testid="export-bookings-csv" onClick={exportBookingsCSV} disabled={!filteredBookings.length} title="Exportar reservas filtradas a CSV" className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Exportar CSV</button>}
            {(isAdmin || isOwner) && <button data-testid="new-booking-button" onClick={() => { if (visibleProperties.length > 0) setNewBooking({ ...newBooking, property_id: visibleProperties[0].id }); setIsBookingModalOpen(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Añadir Reserva</button>}
          </div>
        </div>
        <div className="p-4 bg-slate-50 border-b border-slate-100 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-700 flex-wrap"><Filter className="w-4 h-4" /> Filtros
            <label className="ml-3 flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer"><input type="checkbox" checked={!hidePastBookings} onChange={(e) => setHidePastBookings(!e.target.checked)} className="rounded" /> Incluir pasadas</label>
            <button
              data-testid="sort-toggle"
              onClick={() => setBookingSortDir(bookingSortDir === 'asc' ? 'desc' : 'asc')}
              title={bookingSortDir === 'asc' ? 'Más antiguas arriba — pulsa para invertir' : 'Más recientes arriba — pulsa para invertir'}
              className="ml-2 flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2.5 py-1.5 rounded-lg"
            >
              <Calendar className="w-3.5 h-3.5" />
              {bookingSortDir === 'asc' ? '↑ Antiguas primero' : '↓ Recientes primero'}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="show-all-bookings" onClick={showAll} title="Muestra todas las reservas sin ningún filtro (incluye pasadas)" className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Ver todas
              </button>
              {anyFilterActive && <button data-testid="clear-filters" onClick={clearAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">Limpiar filtros</button>}
            </div>
          </div>
          {(() => {
            const pad = (n) => String(n).padStart(2, '0');
            const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const today = new Date();
            // Semana (lunes-domingo)
            const dow = (today.getDay() + 6) % 7; // 0=lun, 6=dom
            const weekStart = new Date(today); weekStart.setDate(today.getDate() - dow);
            const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
            const nextWeekStart = new Date(weekStart); nextWeekStart.setDate(weekStart.getDate() + 7);
            const nextWeekEnd = new Date(nextWeekStart); nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
            // Mes
            const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            const nextMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
            // Año
            const yearStart = new Date(today.getFullYear(), 0, 1);
            const yearEnd = new Date(today.getFullYear(), 11, 31);
            const nextYearStart = new Date(today.getFullYear() + 1, 0, 1);
            const nextYearEnd = new Date(today.getFullYear() + 1, 11, 31);
            const quicks = [
              { key: 'this-week', label: 'Esta semana', start: iso(weekStart), end: iso(weekEnd) },
              { key: 'next-week', label: 'Próxima semana', start: iso(nextWeekStart), end: iso(nextWeekEnd) },
              { key: 'this-month', label: 'Este mes', start: iso(monthStart), end: iso(monthEnd) },
              { key: 'next-month', label: 'Próximo mes', start: iso(nextMonthStart), end: iso(nextMonthEnd) },
              { key: 'this-year', label: 'Este año', start: iso(yearStart), end: iso(yearEnd) },
              { key: 'next-year', label: 'Próximo año', start: iso(nextYearStart), end: iso(nextYearEnd) },
            ];
            const apply = (q) => {
              // Si ya está activo el mismo filtro, lo quita
              if (bookingFilterRange.label === q.label) {
                setBookingFilterRange({ start: '', end: '', label: '' });
              } else {
                setBookingFilterRange({ start: q.start, end: q.end, label: q.label });
                // Al aplicar un rango que incluye hoy o futuro, aseguramos que se vean todas
                setHidePastBookings(false);
                setBookingFilterDate('');
                setBookingFilterMonth('');
              }
            };
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500 mr-1">Filtros rápidos:</span>
                {quicks.map(q => {
                  const active = bookingFilterRange.label === q.label;
                  return (
                    <button
                      key={q.key}
                      data-testid={`quick-${q.key}`}
                      onClick={() => apply(q)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition ${active ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm' : 'bg-white text-slate-700 border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700'}`}
                    >
                      {q.label}
                    </button>
                  );
                })}
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <span className="text-xs font-medium text-slate-500">Rango:</span>
                <input
                  data-testid="range-from"
                  type="date"
                  value={bookingFilterRange.label === 'Personalizado' ? bookingFilterRange.start : ''}
                  onChange={(e) => {
                    const start = e.target.value;
                    const end = bookingFilterRange.label === 'Personalizado' ? bookingFilterRange.end : '';
                    if (start && end && start > end) {
                      setBookingFilterRange({ start, end: start, label: 'Personalizado' });
                    } else if (start) {
                      setBookingFilterRange({ start, end: end || start, label: 'Personalizado' });
                      setHidePastBookings(false); setBookingFilterDate(''); setBookingFilterMonth('');
                    }
                  }}
                  className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  title="Desde"
                />
                <span className="text-xs text-slate-400">→</span>
                <input
                  data-testid="range-to"
                  type="date"
                  value={bookingFilterRange.label === 'Personalizado' ? bookingFilterRange.end : ''}
                  min={bookingFilterRange.label === 'Personalizado' ? bookingFilterRange.start : undefined}
                  onChange={(e) => {
                    const end = e.target.value;
                    const start = bookingFilterRange.label === 'Personalizado' ? bookingFilterRange.start : '';
                    if (end) {
                      setBookingFilterRange({ start: start || end, end, label: 'Personalizado' });
                      setHidePastBookings(false); setBookingFilterDate(''); setBookingFilterMonth('');
                    }
                  }}
                  className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  title="Hasta"
                />
                {bookingFilterRange.label && (
                  <button data-testid="quick-clear" onClick={() => setBookingFilterRange({ start: '', end: '', label: '' })} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium ml-1">
                    Quitar
                  </button>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Buscar (nombre, teléfono, email, notas)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    data-testid="booking-search"
                    type="text"
                    value={bookingSearchInput}
                    onChange={(e) => setBookingSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applySearch(); } }}
                    placeholder="Escribe y pulsa Buscar o Enter..."
                    className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                  {bookingSearchInput && (
                    <button
                      type="button"
                      data-testid="booking-search-clear"
                      onClick={() => { setBookingSearchInput(''); setBookingSearch(''); }}
                      className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                      title="Limpiar búsqueda"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <button
                  data-testid="booking-search-button"
                  onClick={applySearch}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 shrink-0"
                >
                  <Search className="w-4 h-4" /> Buscar
                </button>
              </div>
              {bookingSearch && <div className="mt-1 text-xs text-slate-500">Filtrando por: <span className="font-semibold text-indigo-700">"{bookingSearch}"</span></div>}
            </div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Día</label><input type="date" value={bookingFilterDate} onChange={(e) => { setBookingFilterDate(e.target.value); setBookingFilterMonth(''); }} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white" /></div>
            <div><label className="block text-xs font-medium text-slate-500 mb-1">Mes</label><input type="month" value={bookingFilterMonth} onChange={(e) => { setBookingFilterMonth(e.target.value); setBookingFilterDate(''); }} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white" /></div>
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Limpiadora</label>
                <select data-testid="filter-cleaner" value={bookingFilterCleaner} onChange={(e) => setBookingFilterCleaner(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                  <option value="">Todas</option>
                  <option value="__none__">Sin asignar</option>
                  {cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {isAdmin && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Recepcionista</label>
                <select data-testid="filter-checkin" value={bookingFilterCheckin} onChange={(e) => setBookingFilterCheckin(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                  <option value="">Todos</option>
                  <option value="__none__">Sin asignar</option>
                  {checkinStaff.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Estado</label>
              <select data-testid="filter-status" value={bookingFilterStatus} onChange={(e) => setBookingFilterStatus(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                <option value="">Todos</option>
                <option value="active">Confirmadas</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <label className="block text-xs font-medium text-slate-500 mb-1">Propiedades</label>
              {(() => {
                const total = visibleProperties.length;
                const selectedCount = total - unselectedProperties.length;
                const allSelected = unselectedProperties.length === 0;
                const q = propSearchQuery.trim().toLowerCase();
                const filteredList = q ? visibleProperties.filter(p => (p.name || '').toLowerCase().includes(q)) : visibleProperties;
                const label = allSelected
                  ? `Todas (${total})`
                  : selectedCount === 0
                    ? 'Ninguna seleccionada'
                    : `${selectedCount} de ${total} seleccionadas`;
                return (
                  <div className="relative">
                    <button
                      type="button"
                      data-testid="prop-filter-dropdown-btn"
                      onClick={() => setShowPropDropdown(v => !v)}
                      className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm bg-white hover:bg-slate-50 transition ${!allSelected ? 'border-indigo-400 text-indigo-700 font-medium' : 'border-slate-300 text-slate-700'}`}
                    >
                      <span className="truncate">{label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform shrink-0 ${showPropDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showPropDropdown && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setShowPropDropdown(false)} />
                        <div data-testid="prop-filter-panel" className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                          <input
                            type="text"
                            data-testid="prop-filter-search"
                            placeholder="Buscar propiedad..."
                            value={propSearchQuery}
                            onChange={(e) => setPropSearchQuery(e.target.value)}
                            className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs mb-2 outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                            <button
                              type="button"
                              data-testid="prop-filter-all"
                              onClick={() => setUnselectedProperties([])}
                              className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium"
                            >Marcar todas</button>
                            <button
                              type="button"
                              data-testid="prop-filter-none"
                              onClick={() => setUnselectedProperties(visibleProperties.map(p => p.id))}
                              className="text-xs px-2 py-1 rounded bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium"
                            >Desmarcar todas</button>
                          </div>
                          <div className="max-h-60 overflow-y-auto space-y-0.5">
                            {filteredList.length === 0 && (
                              <p className="text-xs text-slate-400 italic p-2">Sin coincidencias</p>
                            )}
                            {filteredList.map(p => {
                              const checked = !unselectedProperties.includes(p.id);
                              return (
                                <label key={p.id} data-testid={`prop-filter-option-${p.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                                  <input type="checkbox" checked={checked} onChange={() => toggleProp(p.id)} className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                  <span className="text-xs text-slate-700 truncate flex-1">{p.name}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="pt-2 mt-2 border-t border-slate-100 flex justify-end">
                            <button type="button" onClick={() => setShowPropDropdown(false)} className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 font-medium">Cerrar</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="scroll-x-visible" style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto' }}>
          <table className="w-full text-left border-collapse min-w-[1600px]">
            <thead><tr className="bg-white text-slate-500 text-sm border-b border-slate-100"><th className="p-4 font-medium">Huésped</th><th className="p-4 font-medium">Contacto</th><th className="p-4 font-medium">Propiedad</th><th className="p-4 font-medium">Entrada</th><th className="p-4 font-medium">Salida</th><th className="p-4 font-medium text-center">Nº pers.</th>{isAdmin && <th className="p-4 font-medium">Limpiadora</th>}<th className="p-4 font-medium">Estado</th>{isAdmin && <th className="p-4 font-medium">Recepcionista</th>}<th className="p-4 font-medium">Observaciones</th>{isAdmin && <th className="p-4 font-medium text-right">Precio Servicio</th>}{isAdmin && <th className="p-4 font-medium text-right">Gastos Extras</th>}{isAdmin && <th className="p-4 font-medium text-right">Limpieza €</th>}{isAdmin && <th className="p-4 font-medium text-right">Check-in €</th>}{isAdmin && <th className="p-4 font-medium text-right">Total €</th>}{isAdmin && <th className="p-4 font-medium">Notas</th>}{(isAdmin || isOwner) && <th className="p-4 font-medium text-right">Acciones</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filteredBookings.length === 0 ? <tr><td colSpan="13" className="p-8 text-center text-slate-500">No hay reservas.</td></tr> : filteredBookings.map(b => { const p = getProperty(b.property_id); return (
                <tr key={b.id} className="hover:bg-slate-50/50">
                  <td className="p-4 text-slate-700 font-medium">{b.guest}</td>
                  <td className="p-4 text-slate-600 text-xs">
                    {b.guest_phone ? <div className="flex items-center gap-1"><span>📞</span> <a href={`tel:${b.guest_phone}`} className="text-indigo-600 hover:underline">{b.guest_phone}</a></div> : <span className="text-slate-300 italic">Sin tel.</span>}
                    {b.guest_email && <div className="flex items-center gap-1 mt-0.5 truncate max-w-[160px]" title={b.guest_email}><span>✉️</span> <a href={`mailto:${b.guest_email}`} className="text-indigo-600 hover:underline truncate">{b.guest_email}</a></div>}
                  </td>
                  <td className="p-4 text-slate-700">{p?.name}</td>
                  <td className="p-4 text-slate-700 whitespace-nowrap">
                    {isAdmin ? (
                      editingDate.id === b.id && editingDate.field === 'in' ? (
                        <div className="flex items-center gap-1">
                          <input
                            data-testid={`date-in-input-${b.id}`}
                            type="date" autoFocus
                            value={dateDraft}
                            onChange={(e) => setDateDraft(e.target.value)}
                            className="border border-indigo-300 rounded px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          <button data-testid={`date-in-save-${b.id}`} onClick={() => saveDate(b)} disabled={savingDate} className="p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                            {savingDate ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          </button>
                          <button data-testid={`date-in-cancel-${b.id}`} onClick={cancelDateEditor} className="p-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md"><X className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <button data-testid={`date-in-edit-${b.id}`} onClick={() => openDateEditor(b, 'in')} title="Click para modificar la fecha de entrada" className="text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50 hover:text-indigo-700 transition text-left">
                          {formatDisplayDate(b.check_in)}
                        </button>
                      )
                    ) : formatDisplayDate(b.check_in)}
                  </td>
                  <td className="p-4 text-slate-700 whitespace-nowrap">
                    {isAdmin ? (
                      editingDate.id === b.id && editingDate.field === 'out' ? (
                        <div className="flex items-center gap-1">
                          <input
                            data-testid={`date-out-input-${b.id}`}
                            type="date" autoFocus
                            value={dateDraft}
                            onChange={(e) => setDateDraft(e.target.value)}
                            className="border border-indigo-300 rounded px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          <button data-testid={`date-out-save-${b.id}`} onClick={() => saveDate(b)} disabled={savingDate} className="p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                            {savingDate ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          </button>
                          <button data-testid={`date-out-cancel-${b.id}`} onClick={cancelDateEditor} className="p-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md"><X className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <button data-testid={`date-out-edit-${b.id}`} onClick={() => openDateEditor(b, 'out')} title="Click para modificar la fecha de salida" className="text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50 hover:text-indigo-700 transition text-left">
                          {formatDisplayDate(b.check_out)}
                        </button>
                      )
                    ) : formatDisplayDate(b.check_out)}
                  </td>
                  <td className="p-4 text-slate-700 text-center font-semibold">{b.guest_count ?? 0}</td>
                  {isAdmin && <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <select
                        data-testid={`assign-cleaner-${b.id}`}
                        value={b.assigned_cleaner_id || ''}
                        onChange={(e) => handleAssignCleaner(b, e.target.value)}
                        className={`text-xs border rounded-full py-1.5 px-3 font-semibold cursor-pointer outline-none max-w-[140px] truncate ${b.assigned_cleaner_id ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                      >
                        <option value="">Sin asignar</option>
                        {cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {b.assigned_cleaner_id && (() => {
                        const t = visibleTasks.find(x => x.booking_id === b.id);
                        if (!t) return null;
                        if (t.status === 'completed') {
                          return <span data-testid={`cleaning-status-${b.id}`} title={`Limpieza completada`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /></span>;
                        }
                        if (t.status === 'in-progress') {
                          return <span data-testid={`cleaning-status-${b.id}`} title={`Limpieza en curso`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shrink-0 animate-pulse"><Clock className="w-3.5 h-3.5" /></span>;
                        }
                        return <span data-testid={`cleaning-status-${b.id}`} title={`Limpieza pendiente`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-500 shrink-0"><Clock className="w-3.5 h-3.5" /></span>;
                      })()}
                    </div>
                  </td>}
                  <td className="p-4">
                    {isAdmin ? (
                      <select
                        data-testid={`booking-status-${b.id}`}
                        value={b.status === 'cancelled' ? 'cancelled' : 'confirmed'}
                        onChange={(e) => handleChangeBookingStatus(b, e.target.value)}
                        className={`text-xs border rounded-full py-1.5 px-3 font-semibold cursor-pointer outline-none ${b.status === 'cancelled' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                      >
                        <option value="confirmed">Confirmada</option>
                        <option value="cancelled">Cancelada</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${b.status === 'cancelled' ? 'bg-rose-100 text-rose-700' : b.status === 'arriving' ? 'bg-blue-100 text-blue-700' : b.status === 'departing' ? 'bg-orange-100 text-orange-700' : b.status === 'past' ? 'bg-slate-200 text-slate-500' : 'bg-slate-100 text-slate-700'}`}>{b.status === 'cancelled' ? 'Cancelada' : b.status === 'arriving' ? 'Entrando' : b.status === 'departing' ? 'Saliendo' : b.status === 'past' ? 'Pasada' : 'Próxima'}</span>
                    )}
                  </td>
                  {isAdmin && <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <select
                        data-testid={`assign-checkin-${b.id}`}
                        value={b.assigned_checkin_id || (b.assigned_checkin_name === 'Automático' ? '__auto__' : '')}
                        onChange={(e) => handleAssignCheckin(b, e.target.value)}
                        className={`text-xs border rounded-full py-1.5 px-3 font-semibold cursor-pointer outline-none max-w-[140px] truncate ${b.assigned_checkin_name === 'Automático' ? 'bg-sky-50 text-sky-700 border-sky-200' : b.assigned_checkin_id ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                      >
                        <option value="">Sin asignar</option>
                        <option value="__auto__">🔐 Automático</option>
                        {checkinStaff.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {b.assigned_checkin_id && (() => {
                        const t = visibleCheckinTasks.find(x => x.booking_id === b.id);
                        if (!t) return null;
                        if (t.status === 'completed') {
                          return <span data-testid={`checkin-status-${b.id}`} title={`Check-in completado`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /></span>;
                        }
                        if (t.status === 'in-progress') {
                          return <span data-testid={`checkin-status-${b.id}`} title={`Check-in en curso`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shrink-0 animate-pulse"><Clock className="w-3.5 h-3.5" /></span>;
                        }
                        return <span data-testid={`checkin-status-${b.id}`} title={`Check-in pendiente`} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-500 shrink-0"><Clock className="w-3.5 h-3.5" /></span>;
                      })()}
                    </div>
                  </td>}
                  <td className="p-4 text-slate-500 text-xs max-w-[220px]">
                    {isAdmin ? (
                      editingObsId === b.id ? (
                        <div className="flex flex-col gap-1.5">
                          <textarea
                            data-testid={`obs-textarea-${b.id}`}
                            value={obsDraft}
                            onChange={(e) => setObsDraft(e.target.value)}
                            rows="3"
                            autoFocus
                            placeholder="Añadir observaciones..."
                            className="w-full border border-indigo-300 rounded-md p-1.5 text-xs outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                          />
                          <div className="flex gap-1.5">
                            <button
                              data-testid={`obs-save-${b.id}`}
                              onClick={() => saveObservations(b)}
                              disabled={savingObs}
                              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-2 py-1 rounded-md flex items-center gap-1 disabled:opacity-60"
                            >
                              {savingObs ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Guardar
                            </button>
                            <button
                              data-testid={`obs-cancel-${b.id}`}
                              onClick={cancelObsEditor}
                              disabled={savingObs}
                              className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium px-2 py-1 rounded-md flex items-center gap-1"
                            >
                              <X className="w-3 h-3" /> Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          data-testid={`obs-edit-${b.id}`}
                          onClick={() => openObsEditor(b)}
                          title="Click para editar observaciones"
                          className="w-full text-left group flex items-start gap-1 hover:bg-indigo-50 rounded-md px-1.5 py-1 -mx-1.5 transition"
                        >
                          <span className={`flex-1 line-clamp-2 ${b.observations ? 'text-slate-600' : 'text-slate-300 italic'}`}>
                            {b.observations || 'Añadir nota...'}
                          </span>
                          <MessageSquare className="w-3 h-3 text-slate-300 group-hover:text-indigo-500 flex-shrink-0 mt-0.5" />
                        </button>
                      )
                    ) : (
                      <div className="truncate max-w-[180px]" title={b.observations}>
                        {b.observations || <span className="text-slate-300 italic">Sin notas</span>}
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="p-4 text-right" data-testid={`svc-price-${b.id}`}>
                      <span title={`Limpieza ${getBookingCleaningPrice(b).toFixed(2)}€ + Check-in ${getBookingCheckinPrice(b).toFixed(2)}€`} className={`inline-block text-xs font-semibold px-2.5 py-1.5 rounded-md border ${(getBookingCleaningPrice(b) + getBookingCheckinPrice(b)) > 0 ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {(getBookingCleaningPrice(b) + getBookingCheckinPrice(b)).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  )}
                  {isAdmin && (
                    editingExpId === b.id ? (
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            data-testid={`exp-input-${b.id}`}
                            type="number" min="0" step="0.01" autoFocus
                            value={expDraft}
                            onChange={(e) => setExpDraft(e.target.value)}
                            className="w-20 border border-emerald-300 rounded px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-emerald-500 text-right"
                          />
                          <span className="text-xs text-slate-500">€</span>
                          <button data-testid={`exp-save-${b.id}`} onClick={() => saveExpenses(b)} disabled={savingExp} className="p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                            {savingExp ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          </button>
                          <button data-testid={`exp-cancel-${b.id}`} onClick={cancelExpEditor} className="p-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md"><X className="w-3 h-3" /></button>
                        </div>
                      </td>
                    ) : (
                      <td className="p-4 text-right">
                        <button
                          data-testid={`exp-edit-${b.id}`}
                          onClick={() => openExpEditor(b)}
                          title="Click para editar gastos extras"
                          className={`text-xs font-semibold px-2.5 py-1.5 rounded-md border transition ${(b.extra_expenses ?? 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100 hover:text-slate-600'}`}
                        >
                          {(b.extra_expenses ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                        </button>
                      </td>
                    )
                  )}
                  {isAdmin && (
                    editingClPriceId === b.id ? (
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            data-testid={`clprice-input-${b.id}`}
                            type="number" min="0" step="0.01" autoFocus
                            value={clPriceDraft}
                            onChange={(e) => setClPriceDraft(e.target.value)}
                            className="w-20 border border-emerald-300 rounded px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-emerald-500 text-right"
                          />
                          <span className="text-xs text-slate-500">€</span>
                          <button data-testid={`clprice-save-${b.id}`} onClick={() => saveCleaningPrice(b)} disabled={savingClPrice} className="p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                            {savingClPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          </button>
                          <button data-testid={`clprice-cancel-${b.id}`} onClick={cancelClPriceEditor} className="p-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md"><X className="w-3 h-3" /></button>
                        </div>
                      </td>
                    ) : (
                      <td className="p-4 text-right">
                        <button
                          data-testid={`clprice-edit-${b.id}`}
                          onClick={() => openClPriceEditor(b)}
                          title="Click para editar precio de limpieza"
                          className={`text-xs font-semibold px-2.5 py-1.5 rounded-md border transition ${getBookingCleaningPrice(b) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100 hover:text-slate-600'}`}
                        >
                          {getBookingCleaningPrice(b).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                        </button>
                      </td>
                    )
                  )}
                  {isAdmin && (
                    editingCiPriceId === b.id ? (
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            data-testid={`ciprice-input-${b.id}`}
                            type="number" min="0" step="0.01" autoFocus
                            value={ciPriceDraft}
                            onChange={(e) => setCiPriceDraft(e.target.value)}
                            className="w-20 border border-sky-300 rounded px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-sky-500 text-right"
                          />
                          <span className="text-xs text-slate-500">€</span>
                          <button data-testid={`ciprice-save-${b.id}`} onClick={() => saveCheckinPrice(b)} disabled={savingCiPrice} className="p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-60">
                            {savingCiPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          </button>
                          <button data-testid={`ciprice-cancel-${b.id}`} onClick={cancelCiPriceEditor} className="p-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md"><X className="w-3 h-3" /></button>
                        </div>
                      </td>
                    ) : (
                      <td className="p-4 text-right">
                        <button
                          data-testid={`ciprice-edit-${b.id}`}
                          onClick={() => openCiPriceEditor(b)}
                          title="Click para editar precio de check-in"
                          className={`text-xs font-semibold px-2.5 py-1.5 rounded-md border transition ${getBookingCheckinPrice(b) > 0 ? 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100 hover:text-slate-600'}`}
                        >
                          {getBookingCheckinPrice(b).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                        </button>
                      </td>
                    )
                  )}
                  {isAdmin && (
                    <td className="p-4 text-right" data-testid={`total-${b.id}`}>
                      <span
                        title={`Limpieza ${getBookingCleaningPrice(b).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} + Check-in ${getBookingCheckinPrice(b).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} + Extras ${(Number(b.extra_expenses)||0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}`}
                        className={`inline-block text-xs font-bold px-2.5 py-1.5 rounded-md border ${getBookingTotal(b) > 0 ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                      >
                        {getBookingTotal(b).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  )}
                  {isAdmin && (
                    editingNotesId === b.id ? (
                      <td className="p-4">
                        <div className="flex flex-col gap-1.5">
                          <textarea
                            data-testid={`notes-textarea-${b.id}`}
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows="3"
                            autoFocus
                            placeholder="Nota interna..."
                            className="w-full min-w-[180px] border border-amber-300 rounded-md p-1.5 text-xs outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                          />
                          <div className="flex gap-1.5">
                            <button data-testid={`notes-save-${b.id}`} onClick={() => saveAdminNotes(b)} disabled={savingNotes} className="text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium px-2 py-1 rounded-md flex items-center gap-1 disabled:opacity-60">
                              {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Guardar
                            </button>
                            <button data-testid={`notes-cancel-${b.id}`} onClick={cancelNotesEditor} disabled={savingNotes} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium px-2 py-1 rounded-md flex items-center gap-1">
                              <X className="w-3 h-3" /> Cancelar
                            </button>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <td className="p-4 text-slate-500 text-xs max-w-[200px]">
                        <button
                          data-testid={`notes-edit-${b.id}`}
                          onClick={() => openNotesEditor(b)}
                          title="Click para editar nota interna"
                          className="w-full text-left group flex items-start gap-1 hover:bg-amber-50 rounded-md px-1.5 py-1 -mx-1.5 transition"
                        >
                          <span className={`flex-1 line-clamp-2 ${b.admin_notes ? 'text-amber-800' : 'text-slate-300 italic'}`}>
                            {b.admin_notes || 'Añadir nota interna...'}
                          </span>
                          <MessageSquare className="w-3 h-3 text-slate-300 group-hover:text-amber-500 flex-shrink-0 mt-0.5" />
                        </button>
                      </td>
                    )
                  )}
                  {(isAdmin || isOwner) && <td className="p-4 text-right"><div className="flex justify-end gap-2">
                    {b.history?.length > 0 && <button onClick={() => { setViewingHistoryBooking(b); setIsHistoryModalOpen(true); }} className="text-xs text-slate-600 hover:text-slate-800 font-medium bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-lg" title="Historial"><History className="w-3.5 h-3.5" /></button>}
                    {b.status !== 'cancelled' && <button onClick={() => { setEditingBooking(b); setIsEditBookingModalOpen(true); }} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg">Modificar</button>}
                  </div></td>}
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const TasksView = () => {
    // Filtrado unificado: estado + rango de fechas + propiedades
    const matchesFilters = (t) => {
      if (tasksStatusFilter && t.status !== tasksStatusFilter) return false;
      if (tasksUnselectedProps.includes(t.property_id)) return false;
      if (tasksFilterRange.start && tasksFilterRange.end) {
        if ((t.date || '') < tasksFilterRange.start) return false;
        if ((t.date || '') > tasksFilterRange.end) return false;
      }
      return true;
    };
    const filteredTasks = visibleTasks.filter(matchesFilters);
    // Para limpiadoras: check-ins de las MISMAS propiedades donde tienen tarea; para admin: todos
    const relevantCheckins = isAdmin
      ? visibleCheckinTasks.filter(t => {
          if (tasksUnselectedProps.includes(t.property_id)) return false;
          if (tasksFilterRange.start && tasksFilterRange.end) {
            if ((t.date || '') < tasksFilterRange.start) return false;
            if ((t.date || '') > tasksFilterRange.end) return false;
          }
          return true;
        })
      : visibleCheckinTasks.filter(t => {
          const myPropIds = new Set(visibleTasks.filter(x => x.assigned_to_id === currentUser.id).map(x => x.property_id));
          if (!myPropIds.has(t.property_id)) return false;
          if (tasksFilterRange.start && tasksFilterRange.end) {
            if ((t.date || '') < tasksFilterRange.start) return false;
            if ((t.date || '') > tasksFilterRange.end) return false;
          }
          return true;
        });

    // Filtros rápidos de fechas
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = new Date();
    const dow = (today.getDay() + 6) % 7;
    const weekStart = new Date(today); weekStart.setDate(today.getDate() - dow);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const yearStart = new Date(today.getFullYear(), 0, 1);
    const yearEnd = new Date(today.getFullYear(), 11, 31);
    const todayStr2 = iso(today);
    const quicks = [
      { key: 'today', label: 'Hoy', start: todayStr2, end: todayStr2 },
      { key: 'week', label: 'Esta semana', start: iso(weekStart), end: iso(weekEnd) },
      { key: 'month', label: 'Este mes', start: iso(monthStart), end: iso(monthEnd) },
      { key: 'year', label: 'Este año', start: iso(yearStart), end: iso(yearEnd) },
    ];
    const applyQuick = (q) => {
      if (tasksFilterRange.label === q.label) setTasksFilterRange({ start: '', end: '', label: '' });
      else setTasksFilterRange({ start: q.start, end: q.end, label: q.label });
    };
    const toggleTaskProp = (id) => setTasksUnselectedProps(tasksUnselectedProps.includes(id) ? tasksUnselectedProps.filter(x => x !== id) : [...tasksUnselectedProps, id]);
    const selectedPropsCount = visibleProperties.length - tasksUnselectedProps.length;
    const propLabel = tasksUnselectedProps.length === 0 ? `Todas (${visibleProperties.length})` : `${selectedPropsCount} de ${visibleProperties.length}`;
    const anyFilter = tasksStatusFilter || tasksFilterRange.start || tasksUnselectedProps.length > 0;

    return (
    <div className="space-y-4" data-testid="tasks-view">
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-500" /> {isCleaner ? 'Mi Panel de Limpieza' : 'Tareas de Limpieza'} <span className="text-sm font-normal text-slate-500 ml-1">({filteredTasks.length}{anyFilter ? ` / ${visibleTasks.length}` : ''})</span></h2>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && <div className="flex items-center gap-2">
            <button data-testid="sync-cleaners-button" onClick={handleSyncCleaners} title="Sincronizar limpiadoras asignadas en Reservas → tareas de Limpieza" className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Sincronizar con Reservas</button>
            <button data-testid="new-task-button" onClick={() => { if (visibleProperties.length > 0) setNewTask({ ...newTask, property_id: visibleProperties[0].id }); setIsTaskModalOpen(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva Tarea</button>
          </div>}
        </div>
      </div>
      <div className="p-4 bg-slate-50 border-b border-slate-100 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500 mr-1"><Filter className="w-3.5 h-3.5 inline mr-1" />Fecha:</span>
          {quicks.map(q => {
            const active = tasksFilterRange.label === q.label;
            return <button key={q.key} data-testid={`tasks-quick-${q.key}`} onClick={() => applyQuick(q)} className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition ${active ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-700 border-slate-200 hover:bg-indigo-50'}`}>{q.label}</button>;
          })}
          <span className="w-px h-5 bg-slate-200 mx-1" />
          <span className="text-xs font-medium text-slate-500">Rango:</span>
          <input data-testid="tasks-range-from" type="date" value={tasksFilterRange.label === 'Personalizado' ? tasksFilterRange.start : ''} onChange={(e) => { const s = e.target.value; const en = tasksFilterRange.label === 'Personalizado' ? tasksFilterRange.end : ''; if (s) setTasksFilterRange({ start: s, end: en || s, label: 'Personalizado' }); }} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white" title="Desde" />
          <span className="text-xs text-slate-400">→</span>
          <input data-testid="tasks-range-to" type="date" value={tasksFilterRange.label === 'Personalizado' ? tasksFilterRange.end : ''} min={tasksFilterRange.label === 'Personalizado' ? tasksFilterRange.start : undefined} onChange={(e) => { const en = e.target.value; const s = tasksFilterRange.label === 'Personalizado' ? tasksFilterRange.start : ''; if (en) setTasksFilterRange({ start: s || en, end: en, label: 'Personalizado' }); }} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white" title="Hasta" />
          {tasksFilterRange.label && <button data-testid="tasks-range-clear" onClick={() => setTasksFilterRange({ start: '', end: '', label: '' })} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium ml-1">Quitar</button>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500 mr-1">Propiedades:</span>
          <div className="relative min-w-[200px] max-w-[300px]">
            <button type="button" data-testid="tasks-prop-dropdown" onClick={() => setTasksShowPropDropdown(v => !v)} className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-1.5 text-xs bg-white ${tasksUnselectedProps.length > 0 ? 'border-indigo-400 text-indigo-700 font-medium' : 'border-slate-300 text-slate-700'}`}>
              <span className="truncate">{propLabel}</span>
              <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${tasksShowPropDropdown ? 'rotate-180' : ''}`} />
            </button>
            {tasksShowPropDropdown && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setTasksShowPropDropdown(false)} />
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                    <button type="button" onClick={() => setTasksUnselectedProps([])} className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium">Marcar todas</button>
                    <button type="button" onClick={() => setTasksUnselectedProps(visibleProperties.map(p => p.id))} className="text-xs px-2 py-1 rounded bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium">Desmarcar</button>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-0.5">
                    {visibleProperties.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={!tasksUnselectedProps.includes(p.id)} onChange={() => toggleTaskProp(p.id)} className="w-3.5 h-3.5" />
                        <span className="text-xs text-slate-700 truncate flex-1">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <span className="w-px h-5 bg-slate-200 mx-1" />
          <span className="text-xs font-medium text-slate-500">Estado:</span>
          <select data-testid="tasks-status-filter" value={tasksStatusFilter} onChange={(e) => setTasksStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white">
            <option value="">Todos</option>
            <option value="pending">⏱ Pendientes</option>
            <option value="in-progress">▶ En curso</option>
            <option value="completed">✓ Completadas</option>
          </select>
          {anyFilter && <button data-testid="tasks-clear-all" onClick={() => { setTasksStatusFilter(''); setTasksFilterRange({ start: '', end: '', label: '' }); setTasksUnselectedProps([]); }} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 ml-auto">Limpiar filtros</button>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead><tr className="bg-slate-50 text-slate-500 text-sm border-b border-slate-100">
            <th className="p-4 font-medium">Fecha</th>
            <th className="p-4 font-medium">Propiedad</th>
            <th className="p-4 font-medium">Tipo</th>
            <th className="p-4 font-medium">Asignado</th>
            {isAdmin && <th className="p-4 font-medium">Llegada · Fin</th>}
            <th className="p-4 font-medium">Notas</th>
            <th className="p-4 font-medium">Fotos</th>
            <th className="p-4 font-medium">Fichaje</th>
            {isAdmin && <th className="p-4 font-medium text-center">Eliminar</th>}
          </tr></thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {filteredTasks.length === 0 ? <tr><td colSpan={isAdmin ? 9 : 7} className="p-6 text-center text-slate-500">Sin tareas con los filtros aplicados.</td></tr> : filteredTasks.map(t => { const p = getProperty(t.property_id); const photoCount = (t.photos || []).length; const canManage = isAdmin || (isCleaner && t.assigned_to_id === currentUser.id); return (
              <tr key={t.id} className="hover:bg-slate-50/50">
                <td className="p-4 text-slate-700 font-medium whitespace-nowrap">{formatDisplayDate(t.date)}</td>
                <td className="p-4 text-slate-700">{p?.name}</td>
                <td className="p-4 text-slate-700">{t.type}</td>
                <td className="p-4 text-slate-500">
                  {isAdmin ? (
                    <select
                      data-testid={`reassign-task-cleaner-${t.id}`}
                      value={t.assigned_to_id || ''}
                      onChange={async (e) => {
                        const newId = e.target.value;
                        const cl = cleaners.find(c => c.id === newId);
                        try {
                          await api.patch(`/tasks/${t.id}`, { assigned_to_id: newId, assigned_to_name: cl?.name || '' });
                          await reloadAll();
                        } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
                      }}
                      className={`text-xs border rounded-full py-1.5 px-3 font-semibold cursor-pointer outline-none max-w-[160px] truncate ${t.assigned_to_id ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                      title="Reasignar limpiadora"
                    >
                      <option value="">Sin asignar</option>
                      {cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2"><Users className="w-4 h-4" /> {t.assigned_to_name || <span className="italic text-slate-400">Sin asignar</span>}</div>
                  )}
                </td>
                {isAdmin && (
                  <td className="p-4 text-xs text-slate-600 whitespace-nowrap">
                    <div className="flex items-center gap-1.5"><Play className="w-3 h-3 text-emerald-600" /> <span className="font-semibold">{formatTime(t.started_at)}</span></div>
                    <div className="flex items-center gap-1.5 mt-0.5"><Square className="w-3 h-3 text-rose-600" /> <span className="font-semibold">{formatTime(t.ended_at)}</span></div>
                    {formatDurationBetween(t.started_at, t.ended_at) && <div className="flex items-center gap-1.5 mt-0.5 text-indigo-600"><Timer className="w-3 h-3" /> <span className="font-semibold">{formatDurationBetween(t.started_at, t.ended_at)}</span></div>}
                  </td>
                )}
                <td className="p-4"><div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"><MessageSquare className="w-4 h-4 text-slate-400 shrink-0" /><input type="text" placeholder="Escribir nota..." defaultValue={t.notes || ''} onBlur={(e) => e.target.value !== (t.notes || '') && handleTaskNotesChange(t.id, e.target.value)} disabled={!canManage} className="w-full text-xs bg-transparent outline-none text-slate-700 placeholder:text-slate-400 disabled:cursor-not-allowed min-w-[120px]" /></div></td>
                <td className="p-4">
                  <button data-testid={`photos-btn-${t.id}`} onClick={() => setPhotosTask(t)} className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${photoCount === 0 ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
                    <Camera className="w-3.5 h-3.5" /> {photoCount}/{MAX_PHOTOS}
                  </button>
                </td>
                <td className="p-4">
                  <TaskActions
                    task={t}
                    canManage={canManage}
                    onStart={handleTaskStart}
                    onPause={handleTaskPause}
                    onFinish={handleTaskFinish}
                    onOpenPhotos={setPhotosTask}
                  />
                </td>
                {isAdmin && (
                  <td className="p-4 text-center">
                    <button
                      data-testid={`delete-task-${t.id}`}
                      onClick={() => handleDeleteTask(t)}
                      title="Eliminar tarea"
                      className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 p-2 rounded-lg inline-flex transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </div>
    {/* Sección Check-ins asociados */}
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="tasks-checkins-panel">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <Key className="w-5 h-5 text-purple-500" />
        <h3 className="text-sm font-bold text-slate-800">Check-ins en las mismas fechas y propiedades</h3>
        <span className="text-xs text-slate-500 ml-1">({relevantCheckins.length})</span>
      </div>
      {relevantCheckins.length === 0 ? (
        <p className="p-4 text-sm text-slate-400 italic">Sin check-ins con los filtros aplicados.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase border-b border-slate-100">
              <th className="p-3 font-semibold">Fecha</th>
              <th className="p-3 font-semibold">Hora</th>
              <th className="p-3 font-semibold">Propiedad</th>
              <th className="p-3 font-semibold">Huésped</th>
              <th className="p-3 font-semibold">Tipo</th>
              <th className="p-3 font-semibold">Asignado</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {relevantCheckins.sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || '')).map(t => { const p = getProperty(t.property_id); return (
                <tr key={t.id} className="hover:bg-slate-50/50">
                  <td className="p-3 font-medium text-slate-700 whitespace-nowrap">{formatDisplayDate(t.date)}</td>
                  <td className="p-3 text-slate-600 font-mono">{t.time || '—'}</td>
                  <td className="p-3 text-slate-700">{p?.name}</td>
                  <td className="p-3 text-slate-700">{t.guest || <span className="italic text-slate-400">—</span>}</td>
                  <td className="p-3 text-slate-600 whitespace-nowrap">{t.type === 'automatico' ? <span className="inline-flex items-center gap-1 text-xs"><Lock className="w-3 h-3" /> Self Checkin</span> : <span className="inline-flex items-center gap-1 text-xs"><User className="w-3 h-3" /> Presencial</span>}</td>
                  <td className="p-3 text-slate-600">{t.assigned_to_name || <span className="italic text-slate-400">Sin asignar</span>}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </div>
    );
  };

  const ReceptionView = () => {
    const myTasks = visibleCheckinTasks.filter(t => t.assigned_to_id === currentUser.id);
    const inRange = (t) => {
      if (receptionUnselectedProps.includes(t.property_id)) return false;
      if (receptionFilterRange.start && receptionFilterRange.end) {
        if ((t.date || '') < receptionFilterRange.start) return false;
        if ((t.date || '') > receptionFilterRange.end) return false;
      }
      return true;
    };
    const filteredTasks = myTasks.filter(inRange);
    const groupedByDate = filteredTasks.reduce((acc, t) => {
      const key = t.date;
      if (!acc[key]) acc[key] = [];
      acc[key].push(t);
      return acc;
    }, {});
    const sortedDates = Object.keys(groupedByDate).sort();
    const todayGroup = groupedByDate[todayStr] || [];
    const upcomingDates = sortedDates.filter(d => d > todayStr);
    const pastDates = sortedDates.filter(d => d < todayStr).reverse().slice(0, 7);

    // Filtros rápidos
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const nowD = new Date();
    const dow = (nowD.getDay() + 6) % 7;
    const weekStart = new Date(nowD); weekStart.setDate(nowD.getDate() - dow);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    const monthStart = new Date(nowD.getFullYear(), nowD.getMonth(), 1);
    const monthEnd = new Date(nowD.getFullYear(), nowD.getMonth() + 1, 0);
    const yearStart = new Date(nowD.getFullYear(), 0, 1);
    const yearEnd = new Date(nowD.getFullYear(), 11, 31);
    const nowStr = iso(nowD);
    const quicks = [
      { key: 'today', label: 'Hoy', start: nowStr, end: nowStr },
      { key: 'week', label: 'Esta semana', start: iso(weekStart), end: iso(weekEnd) },
      { key: 'month', label: 'Este mes', start: iso(monthStart), end: iso(monthEnd) },
      { key: 'year', label: 'Este año', start: iso(yearStart), end: iso(yearEnd) },
    ];
    const applyQuick = (q) => {
      if (receptionFilterRange.label === q.label) setReceptionFilterRange({ start: '', end: '', label: '' });
      else setReceptionFilterRange({ start: q.start, end: q.end, label: q.label });
    };
    const toggleReceptionProp = (id) => setReceptionUnselectedProps(receptionUnselectedProps.includes(id) ? receptionUnselectedProps.filter(x => x !== id) : [...receptionUnselectedProps, id]);
    const selectedPropsCount = visibleProperties.length - receptionUnselectedProps.length;
    const propLabel = receptionUnselectedProps.length === 0 ? `Todas (${visibleProperties.length})` : `${selectedPropsCount} de ${visibleProperties.length}`;
    const anyFilter = receptionFilterRange.start || receptionUnselectedProps.length > 0;

    const openReceipt = (t) => {
      const p = getProperty(t.property_id);
      generateReceiptWindow(t, p);
    };

    const renderCard = (t) => {
      const p = getProperty(t.property_id);
      const done = t.status === 'completed';
      return (
        <div key={t.id} data-testid={`reception-card-${t.id}`} className={`bg-white rounded-xl shadow-sm border p-5 flex flex-col ${done ? 'border-slate-200' : 'border-purple-200'}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-0.5">{t.type === 'automatico' ? 'Self Checkin' : 'Presencial'}</p>
              <h3 className="text-lg font-bold text-slate-800">{p?.name || 'Sin propiedad'}</h3>
              <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p?.address || <span className="italic">Sin dirección</span>}</p>
            </div>
            <div className={`text-right ${done ? 'text-emerald-600' : 'text-purple-600'}`}>
              <div className="text-2xl font-bold leading-none">{t.time || '—'}</div>
              <div className="text-xs mt-1">{formatDisplayDate(t.date)}</div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3 mb-3 space-y-1.5">
            <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-slate-400" /> <span className="font-semibold text-slate-700">{t.guest || 'Sin huésped'}</span></div>
            {t.guest_phone ? (
              <a href={`tel:${t.guest_phone}`} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-testid={`reception-phone-${t.id}`}>
                <span className="text-base">📞</span> {t.guest_phone}
              </a>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400 italic"><span>📞</span> Sin teléfono</div>
            )}
            {t.guest_phone && (
              <a href={`https://wa.me/${t.guest_phone.replace(/[^\d+]/g,'')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-800 font-medium" data-testid={`reception-wa-${t.id}`}>
                <MessageSquare className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {t.notes && <div className="text-xs text-slate-600 bg-slate-50 rounded-md p-2 whitespace-pre-line mt-2"><MessageSquare className="w-3 h-3 inline mr-1 text-slate-400" />{t.notes}</div>}
          </div>
          {done ? (
            <div className="mt-auto space-y-2">
              <div className="flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-50 text-emerald-700 font-semibold text-sm"><CheckCircle2 className="w-4 h-4" /> Completado</div>
              <button data-testid={`reception-receipt-${t.id}`} onClick={() => openReceipt(t)} className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-1.5">
                <Mail className="w-4 h-4" /> Ver Recibo
              </button>
            </div>
          ) : (
            <div className="mt-auto grid grid-cols-2 gap-2">
              <button data-testid={`reception-arrived-${t.id}`} onClick={() => handleCheckinArrival(t, true)} className="py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Llegó
              </button>
              <button data-testid={`reception-noshow-${t.id}`} onClick={() => { if (window.confirm('¿Marcar como No Presentado?')) handleCheckinArrival(t, false); }} className="py-2.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold text-sm flex items-center justify-center gap-1.5">
                <X className="w-4 h-4" /> No vino
              </button>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-8" data-testid="reception-view">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Key className="w-6 h-6 text-purple-500" /> Mis Check-ins</h2>
          <p className="text-sm text-slate-500 mt-1">Hola {currentUser.name}, aquí están tus check-ins asignados.</p>
        </div>
        <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 mr-1"><Filter className="w-3.5 h-3.5 inline mr-1" />Fecha:</span>
            {quicks.map(q => {
              const active = receptionFilterRange.label === q.label;
              return <button key={q.key} data-testid={`reception-quick-${q.key}`} onClick={() => applyQuick(q)} className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition ${active ? 'bg-purple-600 text-white border-purple-700' : 'bg-white text-slate-700 border-slate-200 hover:bg-purple-50'}`}>{q.label}</button>;
            })}
            <span className="w-px h-5 bg-slate-200 mx-1" />
            <span className="text-xs font-medium text-slate-500">Rango:</span>
            <input data-testid="reception-range-from" type="date" value={receptionFilterRange.label === 'Personalizado' ? receptionFilterRange.start : ''} onChange={(e) => { const s = e.target.value; const en = receptionFilterRange.label === 'Personalizado' ? receptionFilterRange.end : ''; if (s) setReceptionFilterRange({ start: s, end: en || s, label: 'Personalizado' }); }} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white" title="Desde" />
            <span className="text-xs text-slate-400">→</span>
            <input data-testid="reception-range-to" type="date" value={receptionFilterRange.label === 'Personalizado' ? receptionFilterRange.end : ''} min={receptionFilterRange.label === 'Personalizado' ? receptionFilterRange.start : undefined} onChange={(e) => { const en = e.target.value; const s = receptionFilterRange.label === 'Personalizado' ? receptionFilterRange.start : ''; if (en) setReceptionFilterRange({ start: s || en, end: en, label: 'Personalizado' }); }} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white" title="Hasta" />
            {receptionFilterRange.label && <button data-testid="reception-range-clear" onClick={() => setReceptionFilterRange({ start: '', end: '', label: '' })} className="text-xs text-purple-600 hover:text-purple-800 font-medium ml-1">Quitar</button>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 mr-1">Propiedades:</span>
            <div className="relative min-w-[200px] max-w-[300px]">
              <button type="button" data-testid="reception-prop-dropdown" onClick={() => setReceptionShowPropDropdown(v => !v)} className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-1.5 text-xs bg-white ${receptionUnselectedProps.length > 0 ? 'border-purple-400 text-purple-700 font-medium' : 'border-slate-300 text-slate-700'}`}>
                <span className="truncate">{propLabel}</span>
                <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${receptionShowPropDropdown ? 'rotate-180' : ''}`} />
              </button>
              {receptionShowPropDropdown && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setReceptionShowPropDropdown(false)} />
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                      <button type="button" onClick={() => setReceptionUnselectedProps([])} className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium">Marcar todas</button>
                      <button type="button" onClick={() => setReceptionUnselectedProps(visibleProperties.map(p => p.id))} className="text-xs px-2 py-1 rounded bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium">Desmarcar</button>
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-0.5">
                      {visibleProperties.map(p => (
                        <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={!receptionUnselectedProps.includes(p.id)} onChange={() => toggleReceptionProp(p.id)} className="w-3.5 h-3.5" />
                          <span className="text-xs text-slate-700 truncate flex-1">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {anyFilter && <button data-testid="reception-clear-all" onClick={() => { setReceptionFilterRange({ start: '', end: '', label: '' }); setReceptionUnselectedProps([]); }} className="text-xs font-medium text-purple-600 hover:text-purple-800 ml-auto">Limpiar filtros</button>}
          </div>
        </div>

        {receptionFilterRange.start ? (
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wider text-purple-700 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> {receptionFilterRange.label} <span className="text-slate-400">({filteredTasks.length})</span></h3>
            {filteredTasks.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-100 p-8 text-center text-slate-500">No hay check-ins con los filtros aplicados.</div>
            ) : (
              <div className="space-y-4">
                {sortedDates.map(d => (
                  <div key={d}>
                    <div className="text-xs font-semibold text-slate-500 mb-2">{formatDisplayDate(d)} · {groupedByDate[d].length} check-in{groupedByDate[d].length > 1 ? 's' : ''}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {groupedByDate[d].sort((a,b) => (a.time||'').localeCompare(b.time||'')).map(renderCard)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : (
          <>
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wider text-purple-700 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Hoy · {formatDisplayDate(todayStr)} <span className="text-slate-400">({todayGroup.length})</span></h3>
              {todayGroup.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-100 p-8 text-center text-slate-500">No tienes check-ins programados para hoy.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {todayGroup.sort((a,b) => (a.time||'').localeCompare(b.time||'')).map(renderCard)}
                </div>
              )}
            </section>

            {upcomingDates.length > 0 && (
              <section>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Próximos</h3>
                <div className="space-y-4">
                  {upcomingDates.slice(0, 10).map(d => (
                    <div key={d}>
                      <div className="text-xs font-semibold text-slate-500 mb-2">{formatDisplayDate(d)} · {groupedByDate[d].length} check-in{groupedByDate[d].length > 1 ? 's' : ''}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {groupedByDate[d].sort((a,b) => (a.time||'').localeCompare(b.time||'')).map(renderCard)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {pastDates.length > 0 && (
              <section>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Historial reciente</h3>
                <div className="space-y-4">
                  {pastDates.map(d => (
                    <div key={d}>
                      <div className="text-xs font-semibold text-slate-500 mb-2">{formatDisplayDate(d)}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {groupedByDate[d].sort((a,b) => (a.time||'').localeCompare(b.time||'')).map(renderCard)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    );
  };

  const CheckinsView = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="checkins-view">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Key className="w-5 h-5 text-purple-500" /> Tareas de Check-in</h2>
        {isAdmin && <button data-testid="new-checkin-task-button" onClick={() => { if (visibleProperties.length > 0) { const prop = visibleProperties[0]; setNewCheckinTask({ ...newCheckinTask, property_id: prop.id, type: prop.default_checkin_type, assigned_to_id: '', assigned_to_name: prop.default_checkin_type === 'automatico' ? 'Caja de Seguridad' : '' }); } setIsCheckinTaskModalOpen(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo Check-in</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead><tr className="bg-slate-50 text-slate-500 text-sm border-b border-slate-100"><th className="p-4 font-medium">Fecha/Hora</th><th className="p-4 font-medium">Propiedad</th><th className="p-4 font-medium">Huésped/Tipo</th><th className="p-4 font-medium">Asignado</th><th className="p-4 font-medium">Notas</th><th className="p-4 font-medium">Estado</th><th className="p-4 font-medium text-center">Recibo</th>{isAdmin && <th className="p-4 font-medium text-center">Eliminar</th>}</tr></thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {visibleCheckinTasks.length === 0 ? <tr><td colSpan={isAdmin ? 8 : 7} className="p-6 text-center text-slate-500">No hay tareas.</td></tr> : visibleCheckinTasks.map(t => { const p = getProperty(t.property_id); return (
              <tr key={t.id} className="hover:bg-slate-50/50">
                <td className="p-4 text-slate-700 font-medium whitespace-nowrap">{formatDisplayDate(t.date)}<br /><span className="text-xs text-slate-500">{t.time}</span></td>
                <td className="p-4 text-slate-700">{p?.name}</td>
                <td className="p-4 text-slate-700"><div className="font-medium">{t.guest}</div>{t.type === 'automatico' ? <span className="flex items-center gap-1.5 text-xs text-slate-500 mt-1"><Lock className="w-3 h-3" /> Self Checkin</span> : <span className="flex items-center gap-1.5 text-xs text-indigo-600 mt-1"><User className="w-3 h-3" /> Presencial</span>}</td>
                <td className="p-4 text-slate-500"><div className="flex items-center gap-2">{t.type === 'automatico' ? <Lock className="w-4 h-4" /> : <Users className="w-4 h-4" />} {t.assigned_to_name}</div></td>
                <td className="p-4"><div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"><MessageSquare className="w-4 h-4 text-slate-400 shrink-0" /><input type="text" placeholder="Escribir nota..." defaultValue={t.notes || ''} onBlur={(e) => e.target.value !== (t.notes || '') && handleCheckinNotesChange(t.id, e.target.value)} disabled={!isAdmin && !isCheckinRole} className="w-full text-xs bg-transparent outline-none text-slate-700 placeholder:text-slate-400 disabled:cursor-not-allowed" /></div></td>
                <td className="p-4">{(isAdmin || isCheckinRole) ? <select value={t.status} onChange={(e) => handleCheckinStatusChange(t.id, e.target.value)} className={`text-xs border border-transparent rounded-full py-1.5 px-3 font-semibold cursor-pointer outline-none ${t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : t.status === 'in-progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}><option value="pending">Pendiente</option><option value="in-progress">En curso</option><option value="completed">Completado</option></select> : <span className={`px-3 py-1 text-xs font-medium rounded-full ${t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : t.status === 'in-progress' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>{t.status === 'completed' ? 'Completado' : t.status === 'in-progress' ? 'En curso' : 'Pendiente'}</span>}</td>
                <td className="p-4 text-center">
                  <button data-testid={`checkin-receipt-${t.id}`} onClick={() => generateReceiptWindow(t, p)} title="Ver / Descargar Recibo" className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-2 rounded-lg inline-flex">
                    <Mail className="w-4 h-4" />
                  </button>
                </td>
                {isAdmin && (
                  <td className="p-4 text-center">
                    <button
                      data-testid={`delete-checkin-task-${t.id}`}
                      onClick={() => handleDeleteCheckinTask(t)}
                      title="Eliminar tarea de check-in"
                      className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 p-2 rounded-lg inline-flex transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const CalendarView = () => {
    const dates = [];
    for (let i = 0; i < 21; i++) { const d = new Date(calendarDate); d.setDate(calendarDate.getDate() + i); dates.push(d); }
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex flex-col h-[calc(100vh-8rem)] min-h-[500px]" data-testid="calendar-view">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-500" /> Calendario de Ocupación</h2>
            <div className="hidden md:flex items-center gap-3 text-xs text-slate-600">
              <span className="flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-indigo-600 items-center justify-center"><User className="w-2.5 h-2.5 text-white" /></span> Estancia</span>
              <span className="flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-emerald-500 items-center justify-center"><CheckCircle2 className="w-2.5 h-2.5 text-white" /></span> Limpia</span>
              <span className="flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-rose-500 items-center justify-center"><Trash2 className="w-2.5 h-2.5 text-white" /></span> Sucia</span>
              <span className="flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-amber-500 items-center justify-center"><Clock className="w-2.5 h-2.5 text-white" /></span> Limpiando</span>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-lg border border-slate-200">
            <button onClick={() => { const p = new Date(calendarDate); p.setDate(calendarDate.getDate() - 7); setCalendarDate(p); }} className="p-1.5 hover:bg-white rounded-md"><ChevronLeft className="w-5 h-5 text-slate-600" /></button>
            <button onClick={() => setCalendarDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()))} className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-white rounded-md">Hoy</button>
            <button onClick={() => { const n = new Date(calendarDate); n.setDate(calendarDate.getDate() + 7); setCalendarDate(n); }} className="p-1.5 hover:bg-white rounded-md"><ChevronRight className="w-5 h-5 text-slate-600" /></button>
          </div>
        </div>
        <div className="overflow-auto flex-1 bg-slate-50/30">
          <table className="w-full text-left border-collapse min-w-max">
            <thead><tr>
              <th className="sticky left-0 z-20 bg-white p-4 font-bold text-slate-700 text-sm border-b border-r border-slate-200 min-w-[200px]">Propiedad</th>
              {dates.map((d) => { const ds = formatDate(d); const isToday = ds === todayStr; return <th key={ds} className={`p-2 font-medium text-center text-sm border-b border-r border-slate-200 min-w-[90px] ${isToday ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-slate-500'}`}><div className="text-[10px] uppercase tracking-wider mb-0.5">{d.toLocaleDateString('es-ES', { weekday: 'short' })}</div><div className={`text-lg leading-tight ${isToday ? 'font-bold' : ''}`}>{d.getDate()}</div><div className="text-[10px]">{d.toLocaleDateString('es-ES', { month: 'short' })}</div></th>; })}
            </tr></thead>
            <tbody>
              {visibleProperties.map(prop => {
                const isOccupied = visibleBookings.some(b => b.property_id === prop.id && b.status !== 'cancelled' && (b.check_in || '') <= todayStr && (b.check_out || '') > todayStr);
                let stateBadge;
                if (isOccupied) stateBadge = <span data-testid={`cal-state-${prop.id}`} title="En estancia" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white shrink-0"><User className="w-3.5 h-3.5" /></span>;
                else if (prop.cleaning_status === 'in-progress') stateBadge = <span data-testid={`cal-state-${prop.id}`} title="Limpiando" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shrink-0"><Clock className="w-3.5 h-3.5" /></span>;
                else if (prop.cleaning_status === 'dirty') stateBadge = <span data-testid={`cal-state-${prop.id}`} title="Sucia" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-500 text-white shrink-0"><Trash2 className="w-3.5 h-3.5" /></span>;
                else stateBadge = <span data-testid={`cal-state-${prop.id}`} title="Limpia" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /></span>;
                return (
                <tr key={prop.id} className="bg-white hover:bg-slate-50/50">
                  <td className="sticky left-0 z-10 bg-white p-4 font-medium text-slate-800 text-sm border-b border-r border-slate-200">
                    <div className="flex items-center gap-2">
                      {stateBadge}
                      <div className="truncate max-w-[160px]" title={prop.name}>{prop.name}</div>
                    </div>
                  </td>
                  {dates.map((d) => {
                    const ds = formatDate(d);
                    const booking = visibleBookings.find(b => b.property_id === prop.id && ds >= b.check_in && ds < b.check_out && b.status !== 'cancelled');
                    const isFirst = booking && ds === booking.check_in;
                    return <td key={ds} className="p-1 border-b border-r border-slate-100 h-16 relative">{booking && <div className={`h-full w-full bg-indigo-500 text-white p-1.5 flex flex-col justify-center overflow-hidden ${isFirst ? 'rounded-l-md ml-1' : ''}`}>{isFirst && <span className="text-[11px] font-bold truncate block leading-tight px-1">{booking.guest}</span>}</div>}</td>;
                  })}
                </tr>
                );
              })}
              {visibleProperties.length === 0 && <tr><td colSpan={dates.length + 1} className="p-8 text-center text-slate-500 bg-white">No hay propiedades.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleDeleteCleaner = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name} del equipo de limpieza?\n\nLas tareas y reservas donde estaba asignada quedarán "Sin asignar".`)) return;
    try { await api.delete(`/cleaners/${p.id}`); await reloadAll(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  const handleDeleteCheckinStaff = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name} del equipo de recepción?\n\nLas tareas de check-in y reservas donde estaba asignada quedarán "Sin asignar".`)) return;
    try { await api.delete(`/checkin-staff/${p.id}`); await reloadAll(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };

  const TeamView = () => (
    <div className="space-y-8" data-testid="team-view">
      <TeamSection title="Equipo de Limpieza" icon={<Sparkles className="w-5 h-5 text-rose-500" />} tone="rose" role="cleaner" people={cleaners} onAdd={() => setIsCleanerModalOpen(true)} onEdit={(p) => setEditingUser({ user: p, role: 'cleaner', tone: 'rose' })} onDelete={handleDeleteCleaner} />
      <TeamSection title="Equipo de Recepción" icon={<Key className="w-5 h-5 text-purple-500" />} tone="purple" role="checkin" people={checkinStaff} onAdd={() => setIsCheckinStaffModalOpen(true)} onEdit={(p) => setEditingUser({ user: p, role: 'checkin', tone: 'purple' })} onDelete={handleDeleteCheckinStaff} />
    </div>
  );

  const OwnersView = () => (
    <div className="space-y-6" data-testid="owners-view">
      <div className="flex justify-between items-center flex-wrap gap-3"><h2 className="text-2xl font-bold text-slate-800">Directorio de Propietarios ({owners.length})</h2><button data-testid="new-owner-button" onClick={() => setIsOwnerModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Añadir Propietario</button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {owners.map(o => (
          <div key={o.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 group hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg font-bold shrink-0">{o.name.charAt(0)}</div>
              <div className="min-w-0 flex-1"><h3 className="font-bold text-slate-800 truncate" title={o.name}>{o.name}</h3><p className="text-xs text-slate-500 truncate" title={o.email}>{o.email}</p>{o.phone && <p className="text-xs text-slate-500">{o.phone}</p>}</div>
              <button data-testid={`edit-owner-${o.id}`} onClick={() => setEditingUser({ user: o, role: 'owner', tone: 'indigo' })} className="opacity-0 group-hover:opacity-100 text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg transition-all">Editar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const ReportsView = () => {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    useEffect(() => {
      setLoading(true);
      api.get(`/reports/monthly?month=${month}`).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [month]);
    const IGIC_RATE = 0.07;
    const totalWithIgic = (data?.totals.total || 0) * (1 + IGIC_RATE);
    const igicAmount = (data?.totals.total || 0) * IGIC_RATE;
    const printReport = () => {
      if (!data?.rows) return;
      const monthLabel = new Date(month + '-01').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      const isoStamp = new Date().toLocaleString('es-ES');
      const fmt = (n) => (Number(n) || 0).toFixed(2) + '€';
      const rowsHtml = data.rows.map(r => {
        const pct = Math.round((r.nights_occupied / (r.month_days || 1)) * 100);
        return `<tr>
          <td>${r.property_name || ''}</td>
          <td>${r.owner_name || ''}</td>
          <td class="c">${r.nights_occupied}/${r.month_days}</td>
          <td class="c">${pct}%</td>
          <td class="c">${r.cleanings}</td>
          <td class="r">${fmt(r.cleaning_amount)}</td>
          <td class="c">${r.checkins}</td>
          <td class="r">${fmt(r.checkin_amount)}</td>
          <td class="r">${fmt(r.extras_amount || 0)}</td>
          <td class="r">${fmt(r.total)}</td>
          <td class="r">${fmt(r.total * IGIC_RATE)}</td>
          <td class="r hi">${fmt(r.total * (1 + IGIC_RATE))}</td>
        </tr>`;
      }).join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Reporte ${monthLabel} - Kasaalmar</title>
        <style>
          @media print { @page { size: A4 landscape; margin: 15mm; } }
          body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #1e293b; padding: 20px; }
          .header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #4f46e5; padding-bottom: 12px; margin-bottom: 16px; }
          .header img { width: 60px; height: 60px; object-fit: contain; }
          h1 { color: #4f46e5; margin: 0 0 4px; font-size: 22px; }
          .sub { color: #64748b; font-size: 12px; }
          .kpis { display: flex; gap: 12px; margin: 16px 0; flex-wrap: wrap; }
          .kpi { flex: 1 1 140px; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
          .kpi .lbl { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: .05em; }
          .kpi .val { font-size: 18px; font-weight: 700; color: #1e293b; margin-top: 2px; }
          .kpi.hi { background: #ecfdf5; border-color: #a7f3d0; }
          .kpi.hi .val { color: #065f46; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
          th, td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: left; }
          th { background: #f1f5f9; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #475569; }
          td.c { text-align: center; } td.r { text-align: right; font-family: ui-monospace, monospace; }
          td.hi { background: #ecfdf5; font-weight: 700; color: #065f46; }
          tfoot td { font-weight: 700; background: #f8fafc; border-top: 2px solid #cbd5e1; }
          .foot { margin-top: 20px; font-size: 10px; color: #94a3b8; text-align: center; }
        </style></head><body>
        <div class="header">
          <img src="${window.location.origin}/kasaalmar-logo.jpg" alt="Kasaalmar" onerror="this.style.display='none'" />
          <div>
            <h1>Kasaalmar · Reporte Mensual</h1>
            <div class="sub">${monthLabel} — Generado ${isoStamp}</div>
          </div>
        </div>
        <div class="kpis">
          <div class="kpi"><div class="lbl">Noches ocupadas</div><div class="val">${data.totals.nights_occupied}</div></div>
          <div class="kpi"><div class="lbl">Ocupación media</div><div class="val">${occupancyPct}%</div></div>
          <div class="kpi"><div class="lbl">Limpiezas</div><div class="val">${data.totals.cleanings}</div></div>
          <div class="kpi"><div class="lbl">Check-ins</div><div class="val">${data.totals.checkins}</div></div>
          <div class="kpi"><div class="lbl">Subtotal</div><div class="val">${fmt(data.totals.total)}</div></div>
          <div class="kpi"><div class="lbl">IGIC 7%</div><div class="val">${fmt(igicAmount)}</div></div>
          <div class="kpi hi"><div class="lbl">Total a facturar</div><div class="val">${fmt(totalWithIgic)}</div></div>
        </div>
        <table>
          <thead><tr>
            <th>Propiedad</th><th>Propietario</th>
            <th>Noches</th><th>Ocup.</th>
            <th>Limpiezas</th><th>€ Limp.</th>
            <th>Check-ins</th><th>€ Check-in</th>
            <th>Extras</th><th>Subtotal</th><th>IGIC 7%</th><th>TOTAL</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
          <tfoot><tr>
            <td colspan="2">TOTAL</td>
            <td class="c">${data.totals.nights_occupied}</td>
            <td class="c">${occupancyPct}%</td>
            <td class="c">${data.totals.cleanings}</td>
            <td class="r">${fmt(data.totals.cleaning_amount)}</td>
            <td class="c">${data.totals.checkins}</td>
            <td class="r">${fmt(data.totals.checkin_amount)}</td>
            <td class="r">${fmt(data.totals.extras_amount || 0)}</td>
            <td class="r">${fmt(data.totals.total)}</td>
            <td class="r">${fmt(igicAmount)}</td>
            <td class="r hi">${fmt(totalWithIgic)}</td>
          </tr></tfoot>
        </table>
        <p class="foot">Kasaalmar Holidays · Reporte generado desde el panel de gestión</p>
        <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
        </body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    };
    const exportCSV = () => {
      if (!data?.rows) return;
      const headers = ['Propiedad', 'Propietario', 'Noches ocupadas', 'Ocupación %', 'Nº Limpiezas', 'Total Limpieza', 'Nº Check-ins', 'Total Check-in', 'Gastos Extras', 'Subtotal', 'IGIC 7%', 'TOTAL a facturar'];
      const rows = data.rows.map(r => [r.property_name, r.owner_name, r.nights_occupied, `${Math.round((r.nights_occupied/(r.month_days||1))*100)}%`, r.cleanings, r.cleaning_amount, r.checkins, r.checkin_amount, r.extras_amount || 0, r.total.toFixed(2), (r.total * IGIC_RATE).toFixed(2), (r.total * (1 + IGIC_RATE)).toFixed(2)]);
      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v ?? ''}"`).join(','))].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `kasaalmar-reporte-${month}.csv`; a.click();
    };
    const occupancyPct = data ? Math.round((data.totals.nights_occupied / (data.totals.month_days * Math.max(data.rows.length, 1))) * 100) : 0;
    return (
      <div className="space-y-6" data-testid="reports-view">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Briefcase className="w-6 h-6 text-indigo-500" /> {isOwner ? 'Panel del Propietario' : 'Reporte Mensual'}</h2>
          <div className="flex items-center gap-3">
            <input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
            <button data-testid="export-csv" onClick={exportCSV} disabled={!data?.rows?.length} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">Exportar CSV</button>
            <button data-testid="print-report" onClick={printReport} disabled={!data?.rows?.length} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Imprimir / PDF</button>
          </div>
        </div>
        {loading ? <div className="p-8 text-center text-slate-500">Cargando...</div> : data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <StatCard icon={Home} label="Noches ocupadas" value={data.totals.nights_occupied} tone="indigo" />
              <StatCard icon={Calendar} label="Ocupación media" value={`${occupancyPct}%`} tone="purple" />
              <StatCard icon={Sparkles} label="Limpiezas" value={data.totals.cleanings} tone="rose" />
              <StatCard icon={Key} label="Check-ins" value={data.totals.checkins} tone="amber" />
              <StatCard icon={Briefcase} label="Gastos extras" value={`${(data.totals.extras_amount || 0).toFixed(2)}€`} tone="orange" />
              <StatCard icon={Briefcase} label="Subtotal" value={`${data.totals.total.toFixed(2)}€`} tone="blue" />
              <StatCard icon={Briefcase} label="IGIC (7%)" value={`${igicAmount.toFixed(2)}€`} tone="rose" />
              <StatCard icon={Briefcase} label="TOTAL a facturar" value={`${totalWithIgic.toFixed(2)}€`} tone="emerald" />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase border-b border-slate-100">
                    <th className="p-3 font-semibold w-8"></th>
                    <th className="p-3 font-semibold">Propiedad</th>
                    {isAdmin && <th className="p-3 font-semibold">Propietario</th>}
                    <th className="p-3 font-semibold text-center">Noches</th>
                    <th className="p-3 font-semibold text-center">Ocup.</th>
                    <th className="p-3 font-semibold text-center">Limpiezas</th>
                    <th className="p-3 font-semibold text-right">€ Limp.</th>
                    <th className="p-3 font-semibold text-center">Check-ins</th>
                    <th className="p-3 font-semibold text-right">€ Check-in</th>
                    <th className="p-3 font-semibold text-right">Extras</th>
                    <th className="p-3 font-semibold text-right">Subtotal</th>
                    <th className="p-3 font-semibold text-right">IGIC 7%</th>
                    <th className="p-3 font-semibold text-right bg-emerald-50 text-emerald-800">TOTAL</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {data.rows.length === 0 ? <tr><td colSpan={isAdmin ? 13 : 12} className="p-6 text-center text-slate-500">No hay actividad este mes.</td></tr> : data.rows.map(r => {
                      const pct = Math.round((r.nights_occupied / (r.month_days || 1)) * 100);
                      const expanded = expandedId === r.property_id;
                      return (
                        <React.Fragment key={r.property_id}>
                          <tr className="hover:bg-slate-50/50">
                            <td className="p-3">
                              <button data-testid={`report-expand-${r.property_id}`} onClick={() => setExpandedId(expanded ? null : r.property_id)} className="p-1 rounded hover:bg-slate-100" title={expanded ? 'Ocultar detalles' : 'Ver detalles'}>
                                {expanded ? <ChevronDown className="w-4 h-4 text-slate-600" /> : <ChevronRight className="w-4 h-4 text-slate-600" />}
                              </button>
                            </td>
                            <td className="p-3 text-slate-800 font-medium">{r.property_name}</td>
                            {isAdmin && <td className="p-3 text-slate-600">{r.owner_name || <span className="italic text-slate-400">—</span>}</td>}
                            <td className="p-3 text-center font-mono">{r.nights_occupied}<span className="text-slate-400 text-xs"> /{r.month_days}</span></td>
                            <td className="p-3 text-center font-mono text-slate-700">{pct}%</td>
                            <td className="p-3 text-center font-mono">{r.cleanings}</td>
                            <td className="p-3 text-right font-mono text-slate-700">{r.cleaning_amount.toFixed(2)}€</td>
                            <td className="p-3 text-center font-mono">{r.checkins}</td>
                            <td className="p-3 text-right font-mono text-slate-700">{r.checkin_amount.toFixed(2)}€</td>
                            <td className="p-3 text-right font-mono text-orange-700">{(r.extras_amount || 0).toFixed(2)}€</td>
                            <td className="p-3 text-right font-mono text-slate-700">{r.total.toFixed(2)}€</td>
                            <td className="p-3 text-right font-mono text-slate-500">{(r.total * IGIC_RATE).toFixed(2)}€</td>
                            <td className="p-3 text-right font-mono font-bold text-emerald-700 bg-emerald-50/50">{(r.total * (1 + IGIC_RATE)).toFixed(2)}€</td>
                          </tr>
                          {expanded && (
                            <tr data-testid={`report-detail-${r.property_id}`} className="bg-slate-50/60"><td colSpan={isAdmin ? 11 : 10} className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-xs font-bold text-rose-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Limpiezas ({r.cleanings_list.length})</h4>
                                  {r.cleanings_list.length === 0 ? <p className="text-xs italic text-slate-400">Sin limpiezas este mes.</p> : (
                                    <ul className="space-y-1">
                                      {r.cleanings_list.map((c) => (
                                        <li key={`${c.date}-${c.staff || 'na'}-${c.guest || 'na'}`} className="text-xs bg-white border border-slate-100 rounded px-2 py-1.5">
                                          <div className="flex justify-between items-center">
                                            <span className="font-mono text-slate-700">{formatDisplayDate(c.date)}</span>
                                            <span className="text-slate-500 truncate ml-2">{c.staff || <em className="text-slate-400">sin asignar</em>}</span>
                                          </div>
                                          {(c.started_at || c.ended_at) && (
                                            <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono mt-0.5">
                                              {c.started_at && <span className="flex items-center gap-0.5"><Play className="w-2.5 h-2.5 text-emerald-500" />{formatTime(c.started_at)}</span>}
                                              {c.ended_at && <span className="flex items-center gap-0.5"><Square className="w-2.5 h-2.5 text-rose-500" />{formatTime(c.ended_at)}</span>}
                                              {formatDurationBetween(c.started_at, c.ended_at) && <span className="flex items-center gap-0.5 text-indigo-500 font-semibold"><Timer className="w-2.5 h-2.5" />{formatDurationBetween(c.started_at, c.ended_at)}</span>}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <h4 className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Key className="w-3.5 h-3.5" /> Check-ins ({r.checkins_list.length})</h4>
                                  {r.checkins_list.length === 0 ? <p className="text-xs italic text-slate-400">Sin check-ins este mes.</p> : (
                                    <ul className="space-y-1">
                                      {r.checkins_list.map((c) => (
                                        <li key={`${c.date}-${c.time || 'na'}-${c.staff || 'na'}`} className="text-xs bg-white border border-slate-100 rounded px-2 py-1.5">
                                          <div className="flex justify-between items-center">
                                            <span className="font-mono text-slate-700">{formatDisplayDate(c.date)}{c.time ? ` · ${c.time}` : ''}</span>
                                            <span className="text-slate-500 truncate ml-2 flex items-center gap-1">
                                              {c.type === 'automatico' ? <Lock className="w-3 h-3" /> : <User className="w-3 h-3" />}
                                              {c.staff || <em className="text-slate-400">sin asignar</em>}
                                            </span>
                                          </div>
                                          {c.completed_at && (
                                            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono mt-0.5">
                                              <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> Fichado: {formatTime(c.completed_at)}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {data.rows.length > 0 && <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold"><tr>
                    <td className="p-3"></td>
                    <td className="p-3" colSpan={isAdmin ? 2 : 1}>TOTAL</td>
                    <td className="p-3 text-center font-mono">{data.totals.nights_occupied}</td>
                    <td className="p-3 text-center font-mono">{occupancyPct}%</td>
                    <td className="p-3 text-center font-mono">{data.totals.cleanings}</td>
                    <td className="p-3 text-right font-mono">{data.totals.cleaning_amount.toFixed(2)}€</td>
                    <td className="p-3 text-center font-mono">{data.totals.checkins}</td>
                    <td className="p-3 text-right font-mono">{data.totals.checkin_amount.toFixed(2)}€</td>
                    <td className="p-3 text-right font-mono text-orange-700">{(data.totals.extras_amount || 0).toFixed(2)}€</td>
                    <td className="p-3 text-right font-mono text-slate-700">{data.totals.total.toFixed(2)}€</td>
                    <td className="p-3 text-right font-mono text-slate-500">{igicAmount.toFixed(2)}€</td>
                    <td className="p-3 text-right font-mono text-emerald-700 bg-emerald-50">{totalWithIgic.toFixed(2)}€</td>
                  </tr></tfoot>}
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const navItems = [
    { id: 'dashboard', label: 'Resumen', icon: LayoutDashboard, roles: ['admin', 'cleaner', 'checkin', 'owner'] },
    { id: 'reception', label: 'Mis Check-ins', icon: Key, roles: ['checkin'] },
    { id: 'calendar', label: 'Calendario', icon: Calendar, roles: ['admin', 'owner'] },
    { id: 'properties', label: 'Propiedades', icon: Home, roles: ['admin', 'cleaner', 'checkin', 'owner'] },
    { id: 'bookings', label: 'Reservas', icon: CalendarDays, roles: ['admin', 'owner'] },
    { id: 'checkins', label: 'Check-ins', icon: Key, roles: ['admin'] },
    { id: 'tasks', label: 'Limpieza', icon: Sparkles, roles: ['admin', 'cleaner'] },
    { id: 'reports', label: 'Reportes', icon: Briefcase, roles: ['admin', 'owner'] },
    { id: 'team', label: 'Personal', icon: UserPlus, roles: ['admin'] },
    { id: 'owners', label: 'Propietarios', icon: Briefcase, roles: ['admin'] },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex text-slate-900 font-sans" data-testid="app-shell">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:translate-x-0 lg:static flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center px-6 border-b border-slate-100 justify-between lg:justify-start shrink-0"><h1 className="text-xl font-black text-indigo-600 flex items-center gap-2"><img src="/kasaalmar-logo.jpg" alt="Kasaalmar" className="w-8 h-8 rounded object-contain" /> Kasaalmar</h1><button className="lg:hidden text-slate-400" onClick={() => setIsMobileMenuOpen(false)}><X className="w-6 h-6" /></button></div>
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.filter(i => i.roles.includes(currentUser.role)).map(item => { const Icon = item.icon; const active = currentTab === item.id; return (
            <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => { setCurrentTab(item.id); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${active ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
              <Icon className={`w-5 h-5 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />{item.label}
            </button>
          ); })}
        </nav>
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg">{currentUser.name?.charAt(0) || 'U'}</div><div className="flex-1 min-w-0"><p className="text-sm font-bold text-slate-900 truncate">{currentUser.name}</p><p className="text-xs text-slate-500 capitalize">{currentUser.role === 'admin' ? 'Administrador' : currentUser.role === 'owner' ? 'Propietario' : currentUser.role === 'cleaner' ? 'Limpiador/a' : 'Recepción'}</p></div></div>
          <button data-testid="change-password-button" onClick={() => setIsChangePasswordOpen(true)} className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:text-indigo-600"><LockKeyhole className="w-4 h-4" /> Cambiar contraseña</button>
          <button data-testid="logout-button" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:text-rose-600"><LogOut className="w-4 h-4" /> Cerrar Sesión</button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 lg:px-8 shrink-0">
          <div className="flex items-center gap-4 lg:hidden"><button onClick={() => setIsMobileMenuOpen(true)}><Menu className="w-6 h-6 text-slate-500" /></button><h1 className="text-lg font-bold text-slate-800">Kasaalmar</h1></div>
          <div className="hidden lg:flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg w-96"><Search className="w-4 h-4 text-slate-400" /><input placeholder="Buscar..." className="bg-transparent outline-none text-sm w-full" /></div>
          <button className="text-slate-400 hover:text-slate-600"><Bell className="w-5 h-5" /></button>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {globalError && <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{globalError}</div>}
            {currentTab === 'dashboard' && <DashboardView />}
            {currentTab === 'reception' && <ReceptionView />}
            {currentTab === 'calendar' && <CalendarView />}
            {currentTab === 'properties' && <PropertiesView />}
            {currentTab === 'bookings' && <BookingsView />}
            {currentTab === 'checkins' && <CheckinsView />}
            {currentTab === 'tasks' && <TasksView />}
            {currentTab === 'reports' && <ReportsView />}
            {currentTab === 'team' && <TeamView />}
            {currentTab === 'owners' && <OwnersView />}
          </div>
        </main>
      </div>

      {/* ============ MODALES ============ */}
      {photosTask && <PhotosModal task={tasks.find(t => t.id === photosTask.id) || photosTask} currentUser={currentUser} onClose={() => { setPhotosTask(null); reloadAll(); }} onChanged={reloadAll} />}

      {isBookingModalOpen && (isAdmin || isOwner) && (
        <Modal title="Nueva Reserva" onClose={() => setIsBookingModalOpen(false)}>
          <form onSubmit={handleAddBooking} className="p-5 space-y-4">
            <Field label="Propiedad"><select required value={newBooking.property_id} onChange={(e) => setNewBooking({ ...newBooking, property_id: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="" disabled>Selecciona</option>{visibleProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
            <Field label="Nombre del Huésped"><input type="text" required value={newBooking.guest} onChange={(e) => setNewBooking({ ...newBooking, guest: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Teléfono huésped"><input data-testid="booking-phone" type="tel" placeholder="+34..." value={newBooking.guest_phone} onChange={(e) => setNewBooking({ ...newBooking, guest_phone: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
              <Field label="Nº personas"><input data-testid="booking-guest-count" type="number" min="0" value={newBooking.guest_count} onChange={(e) => setNewBooking({ ...newBooking, guest_count: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            </div>
            <Field label="Email huésped (opcional)"><input data-testid="booking-email" type="email" placeholder="email@ejemplo.com" value={newBooking.guest_email} onChange={(e) => setNewBooking({ ...newBooking, guest_email: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <div className="grid grid-cols-2 gap-4"><Field label="Entrada"><input type="date" required value={newBooking.check_in} onChange={(e) => setNewBooking({ ...newBooking, check_in: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field><Field label="Salida"><input type="date" required min={newBooking.check_in} value={newBooking.check_out} onChange={(e) => setNewBooking({ ...newBooking, check_out: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field></div>
            <Field label="Observaciones importantes"><textarea placeholder="Ej: Vuelo llega a las 23:00h, necesita cuna..." value={newBooking.observations} onChange={(e) => setNewBooking({ ...newBooking, observations: e.target.value })} rows="3" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-none" /></Field>
            {isAdmin && <Field label="🧹 Asignar limpiadora (opcional — creará tarea para el día de salida)"><select data-testid="booking-cleaner" value={newBooking.assigned_cleaner_id} onChange={(e) => setNewBooking({ ...newBooking, assigned_cleaner_id: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="">Sin asignar</option>{cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
            {isAdmin && <Field label="🔑 Asignar recepcionista (opcional — creará tarea de check-in para el día de entrada)"><select data-testid="booking-checkin" value={newBooking.assigned_checkin_id} onChange={(e) => setNewBooking({ ...newBooking, assigned_checkin_id: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="">Sin asignar</option><option value="__auto__">🔐 Automático (Self Checkin)</option>{checkinStaff.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
            <FormActions onCancel={() => setIsBookingModalOpen(false)} submitLabel="Guardar" />
          </form>
        </Modal>
      )}

      {isEditBookingModalOpen && editingBooking && (
        <Modal title="Modificar Reserva" onClose={() => { setIsEditBookingModalOpen(false); setEditingBooking(null); }}>
          <form onSubmit={handleEditBooking} className="p-5 space-y-4">
            <Field label="Huésped"><input type="text" required value={editingBooking.guest} onChange={(e) => setEditingBooking({ ...editingBooking, guest: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Teléfono"><input type="tel" placeholder="+34..." value={editingBooking.guest_phone || ''} onChange={(e) => setEditingBooking({ ...editingBooking, guest_phone: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
              <Field label="Nº personas"><input type="number" min="0" value={editingBooking.guest_count ?? 0} onChange={(e) => setEditingBooking({ ...editingBooking, guest_count: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            </div>
            <Field label="Email (opcional)"><input type="email" placeholder="email@ejemplo.com" value={editingBooking.guest_email || ''} onChange={(e) => setEditingBooking({ ...editingBooking, guest_email: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <div className="grid grid-cols-2 gap-4"><Field label="Entrada"><input type="date" required value={editingBooking.check_in} onChange={(e) => setEditingBooking({ ...editingBooking, check_in: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field><Field label="Salida"><input type="date" required min={editingBooking.check_in} value={editingBooking.check_out} onChange={(e) => setEditingBooking({ ...editingBooking, check_out: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field></div>
            <Field label="Observaciones importantes"><textarea value={editingBooking.observations || ''} onChange={(e) => setEditingBooking({ ...editingBooking, observations: e.target.value })} rows="3" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-none" /></Field>
            {isAdmin && <Field label="🧹 Limpiadora asignada"><select value={editingBooking.assigned_cleaner_id || ''} onChange={(e) => setEditingBooking({ ...editingBooking, assigned_cleaner_id: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="">Sin asignar</option>{cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
            <FormActions onCancel={() => { setIsEditBookingModalOpen(false); setEditingBooking(null); }} submitLabel="Guardar Cambios" />
          </form>
        </Modal>
      )}

      {isHistoryModalOpen && viewingHistoryBooking && (
        <Modal title="Historial de Cambios" icon={<History className="w-5 h-5 text-indigo-500" />} onClose={() => { setIsHistoryModalOpen(false); setViewingHistoryBooking(null); }}>
          <div className="p-5 max-h-96 overflow-y-auto space-y-4">
            {viewingHistoryBooking.history?.length > 0 ? [...viewingHistoryBooking.history].reverse().map((e) => (
              <div key={e.timestamp || e.id} className="relative pl-6 border-l-2 border-slate-200 pb-4 last:border-transparent last:pb-0">
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-indigo-500" /></div>
                <div className="flex justify-between mb-1"><span className="text-sm font-bold text-slate-800">{e.modified_by}</span><span className="text-xs text-slate-500">{new Date(e.timestamp).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</span></div>
                <ul className="space-y-1">{e.details.map((d, j) => <li key={`${e.timestamp}-${j}`} className="text-sm text-slate-600 flex gap-2"><span className="text-indigo-400">•</span><span>{d}</span></li>)}</ul>
              </div>
            )) : <p className="text-center text-sm text-slate-500">Sin cambios registrados.</p>}
          </div>
        </Modal>
      )}

      {isPropertyModalOpen && isAdmin && (
        <Modal title={editingProperty ? 'Editar Propiedad' : 'Nueva Propiedad'} onClose={() => { setIsPropertyModalOpen(false); setEditingProperty(null); }}>
          <form onSubmit={handleAddProperty} className="p-5 space-y-4">
            <Field label="Nombre"><input type="text" required value={newProperty.name} onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <Field label="Propietario Asignado"><select value={newProperty.owner_id} onChange={(e) => { const o = owners.find(x => x.id === e.target.value); setNewProperty({ ...newProperty, owner_id: e.target.value, owner_name: o?.name || '' }); }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="">Sin propietario</option>{owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></Field>
            <Field label="Dirección"><input type="text" value={newProperty.address} onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <Field label="Tipo de Check-in"><select data-testid="property-checkin-type-select" value={newProperty.default_checkin_type} onChange={(e) => setNewProperty({ ...newProperty, default_checkin_type: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="presencial">👤 Presencial</option><option value="automatico">🔐 Self Checkin</option></select></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tarifa Limpieza (€)"><input type="number" min="0" step="0.01" required value={newProperty.cleaning_price} onChange={(e) => setNewProperty({ ...newProperty, cleaning_price: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
              <Field label="Tarifa Check-in (€)"><input type="number" min="0" step="0.01" required value={newProperty.checkin_price} onChange={(e) => setNewProperty({ ...newProperty, checkin_price: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            </div>
            <FormActions onCancel={() => { setIsPropertyModalOpen(false); setEditingProperty(null); }} submitLabel="Guardar" />
          </form>
        </Modal>
      )}

      {isTaskModalOpen && isAdmin && (
        <Modal title="Nueva Tarea de Limpieza" onClose={() => setIsTaskModalOpen(false)}>
          <form onSubmit={handleAddTask} className="p-5 space-y-4">
            <Field label="Propiedad"><select required value={newTask.property_id} onChange={(e) => {
              const propertyId = e.target.value;
              // Try to auto-fill cleaner from matching booking
              const match = bookings.find(b => b.property_id === propertyId && b.assigned_cleaner_id && b.status !== 'cancelled' && (!newTask.date || b.check_out === newTask.date));
              setNewTask({ ...newTask, property_id: propertyId, ...(match ? { assigned_to_id: match.assigned_cleaner_id, assigned_to_name: match.assigned_cleaner_name } : {}) });
            }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="" disabled>Selecciona</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
            <Field label="Fecha"><input type="date" required value={newTask.date} onChange={(e) => {
              const date = e.target.value;
              // Try to auto-fill cleaner from matching booking for this property + checkout date
              const match = bookings.find(b => b.property_id === newTask.property_id && b.check_out === date && b.assigned_cleaner_id && b.status !== 'cancelled');
              setNewTask({ ...newTask, date, ...(match ? { assigned_to_id: match.assigned_cleaner_id, assigned_to_name: match.assigned_cleaner_name, type: 'Limpieza Salida' } : {}) });
            }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <Field label="Tipo de Tarea"><select required value={newTask.type} onChange={(e) => setNewTask({ ...newTask, type: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option>Limpieza General</option><option>Limpieza Salida</option><option>Repaso Entrada</option></select></Field>
            {(() => {
              const match = newTask.property_id && newTask.date && bookings.find(b => b.property_id === newTask.property_id && b.check_out === newTask.date && b.assigned_cleaner_id && b.status !== 'cancelled');
              return match ? (
                <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Limpiadora <strong>{match.assigned_cleaner_name}</strong> autoasignada desde la reserva de <strong>{match.guest}</strong>.</span>
                </div>
              ) : null;
            })()}
            <Field label="Asignar a"><select required value={newTask.assigned_to_id} onChange={(e) => { const cl = cleaners.find(c => c.id === e.target.value); setNewTask({ ...newTask, assigned_to_id: e.target.value, assigned_to_name: cl?.name || '' }); }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="" disabled>Seleccionar Personal</option>{cleaners.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
            <FormActions onCancel={() => setIsTaskModalOpen(false)} submitLabel="Crear Tarea" />
          </form>
        </Modal>
      )}

      {isCheckinTaskModalOpen && isAdmin && (
        <Modal title="Programar Check-in" onClose={() => setIsCheckinTaskModalOpen(false)}>
          <form onSubmit={handleAddCheckinTask} className="p-5 space-y-4">
            <Field label="Propiedad"><select required value={newCheckinTask.property_id} onChange={(e) => { const prop = properties.find(p => p.id === e.target.value); setNewCheckinTask({ ...newCheckinTask, property_id: prop.id, type: prop.default_checkin_type, assigned_to_id: '', assigned_to_name: prop.default_checkin_type === 'automatico' ? 'Caja de Seguridad' : '' }); }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="" disabled>Selecciona</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
            <Field label="Tipo"><select disabled value={newCheckinTask.type} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none bg-slate-50 text-slate-500 cursor-not-allowed"><option value="presencial">👤 Presencial</option><option value="automatico">🔐 Self Checkin</option></select></Field>
            <Field label="Huésped"><input type="text" required value={newCheckinTask.guest} onChange={(e) => setNewCheckinTask({ ...newCheckinTask, guest: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <div className="grid grid-cols-2 gap-4"><Field label="Fecha"><input type="date" required value={newCheckinTask.date} onChange={(e) => setNewCheckinTask({ ...newCheckinTask, date: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field><Field label="Hora"><input type="time" required value={newCheckinTask.time} onChange={(e) => setNewCheckinTask({ ...newCheckinTask, time: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field></div>
            {newCheckinTask.type === 'presencial' && <Field label="Asignar a"><select required value={newCheckinTask.assigned_to_id} onChange={(e) => setNewCheckinTask({ ...newCheckinTask, assigned_to_id: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"><option value="" disabled>Seleccionar Recepcionista</option>{checkinStaff.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
            <FormActions onCancel={() => setIsCheckinTaskModalOpen(false)} submitLabel="Guardar" />
          </form>
        </Modal>
      )}

      {isOwnerModalOpen && isAdmin && <UserModal title="Añadir Propietario" data={newOwner} setData={setNewOwner} onSubmit={handleAddOwner} onClose={() => setIsOwnerModalOpen(false)} />}
      {isCleanerModalOpen && isAdmin && <UserModal title="Añadir Limpiador/a" data={newCleaner} setData={setNewCleaner} onSubmit={handleAddCleaner} onClose={() => setIsCleanerModalOpen(false)} />}
      {isCheckinStaffModalOpen && isAdmin && <UserModal title="Añadir Recepcionista" data={newCheckinStaff} setData={setNewCheckinStaff} onSubmit={handleAddCheckinStaff} onClose={() => setIsCheckinStaffModalOpen(false)} />}
      {editingUser && isAdmin && <EditUserModal editing={editingUser} onSave={handleSaveEditUser} onDelete={handleDeleteUser} onClose={() => setEditingUser(null)} />}

      {isChangePasswordOpen && <ChangePasswordModal onClose={() => setIsChangePasswordOpen(false)} />}

      {mergingProperty && isAdmin && <MergePropertyModal source={mergingProperty} properties={properties} bookingCount={visibleBookings.filter(b => b.property_id === mergingProperty.id).length} onClose={() => setMergingProperty(null)} onDone={async () => { setMergingProperty(null); await reloadAll(); }} />}

      {propertyPhotosProperty && isAdmin && <PropertyPhotosModal property={properties.find(pp => pp.id === propertyPhotosProperty.id) || propertyPhotosProperty} onClose={async () => { setPropertyPhotosProperty(null); await reloadAll(); }} onChanged={reloadAll} />}

      {isMobileMenuOpen && <div className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />}
    </div>
  );
}

// ============================================================
// SMALL SHARED COMPONENTS
// ============================================================
function StatCard({ icon: Icon, label, value, tone, onClick, cta }) {
  const tones = { blue: 'bg-blue-50 text-blue-600', orange: 'bg-orange-50 text-orange-600', purple: 'bg-purple-50 text-purple-600', rose: 'bg-rose-50 text-rose-600', emerald: 'bg-emerald-50 text-emerald-600' };
  const clickable = !!onClick;
  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? { onClick, type: 'button', 'data-testid': `stat-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex items-center space-x-3 text-left w-full transition-all ${clickable ? 'hover:shadow-md hover:border-indigo-200 hover:-translate-y-0.5 cursor-pointer group' : ''}`}
    >
      <div className={`p-3 rounded-lg ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="flex-1">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <h3 className="text-xl font-bold text-slate-800">{value}</h3>
        {clickable && cta && <p className="text-[10px] font-semibold text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 uppercase tracking-wider">{cta} →</p>}
      </div>
    </Wrapper>
  );
}

function Panel({ title, icon, right, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex flex-col">
      <div className="p-5 border-b border-slate-100 flex justify-between items-center"><h3 className="font-semibold text-slate-800 flex items-center gap-2">{icon} {title}</h3>{right}</div>
      <div className="divide-y divide-slate-100 flex-1 overflow-y-auto max-h-96">{children}</div>
    </div>
  );
}

function TeamSection({ title, icon, tone, people, onAdd, onEdit, onDelete, role }) {
  const toneMap = { rose: 'bg-rose-100 text-rose-600', purple: 'bg-purple-100 text-purple-600' };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center"><h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">{icon} {title} <span className="text-sm font-normal text-slate-500">({people.length})</span></h2><button onClick={onAdd} className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Añadir</button></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{people.map(p => (
        <div key={p.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex items-center gap-4 group hover:shadow-md transition-shadow">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shrink-0 ${toneMap[tone]}`}>{p.name.charAt(0)}</div>
          <div className="min-w-0 flex-1"><h3 className="font-bold text-slate-800 truncate">{p.name}</h3><p className="text-xs text-slate-500 truncate" title={p.email}>{p.email}</p>{p.phone && <p className="text-xs text-slate-500">{p.phone}</p>}</div>
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all">
            {onEdit && <button data-testid={`edit-${role}-${p.id}`} onClick={() => onEdit(p)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg">Editar</button>}
            {onDelete && <button data-testid={`delete-${role}-${p.id}`} onClick={() => onDelete(p)} className="text-xs text-rose-600 hover:text-rose-800 font-medium bg-rose-50 hover:bg-rose-100 px-2.5 py-1 rounded-lg flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /></button>}
          </div>
        </div>
      ))}</div>
    </div>
  );
}

function Modal({ title, icon, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0"><h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">{icon}{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div>
        <div className="overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>{children}</div>;
}

function FormActions({ onCancel, submitLabel }) {
  return (
    <div className="pt-4 flex justify-end gap-3"><button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button><button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">{submitLabel}</button></div>
  );
}

function UserModal({ title, data, setData, onSubmit, onClose }) {
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={onSubmit} className="p-5 space-y-4">
        <Field label="Nombre Completo"><input type="text" required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <Field label="Email (será su login)"><input type="email" required value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <Field label="Teléfono"><input type="tel" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">La contraseña inicial será <code className="bg-white px-1.5 py-0.5 rounded text-indigo-600 font-mono">Kasaalmar2026!</code></div>
        <FormActions onCancel={onClose} submitLabel="Guardar" />
      </form>
    </Modal>
  );
}

function PropertySlideshow({ photos, fallback, name }) {
  const list = (photos && photos.length > 0) ? photos.map(p => p.data_url) : (fallback ? [fallback] : []);
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (list.length <= 1) return;
    const id = setInterval(() => setIdx(i => (i + 1) % list.length), 3500);
    return () => clearInterval(id);
  }, [list.length]);
  if (list.length === 0) {
    return <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">Sin fotos</div>;
  }
  return (
    <>
      {list.map((src, i) => (
        <img key={`img-${i}-${src.slice(0, 32)}`} src={src} alt={`${name}-${i}`} className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`} />
      ))}
      {list.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
          {list.map((src, i) => <span key={`dot-${i}-${src.slice(0, 12)}`} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/40'}`} />)}
        </div>
      )}
    </>
  );
}

function PropertyPhotosModal({ property, onClose, onChanged }) {
  const [photos, setPhotos] = React.useState(property.photos || []);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [viewIdx, setViewIdx] = React.useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (photos.length >= 10) { setError('Máximo 10 fotos por propiedad'); return; }
    setUploading(true); setError('');
    try {
      const dataUrl = await fileToCompressedDataUrl(file, 1600, 0.75);
      await api.post(`/properties/${property.id}/photos`, { data_url: dataUrl });
      const newPhoto = { data_url: dataUrl, uploaded_at: new Date().toISOString() };
      setPhotos([...photos, newPhoto]);
      onChanged?.();
    } catch (err) {
      const backendDetail = err.response?.data?.detail;
      const clientMessage = err?.message;
      const msg = backendDetail != null ? formatApiError(backendDetail) : (clientMessage || 'Error al subir foto');
      setError(msg);
    } finally { setUploading(false); }
  };

  const removePhoto = async (idx) => {
    if (photos.length <= 1) {
      if (!window.confirm('Esta es la última foto de la propiedad. ¿Seguro que quieres eliminarla? La propiedad quedará sin portada.')) return;
    } else if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await api.delete(`/properties/${property.id}/photos/${idx}`);
      setPhotos(photos.filter((_, i) => i !== idx));
      onChanged?.();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const setAsCover = async (idx) => {
    if (idx === 0) return;
    try {
      await api.post(`/properties/${property.id}/photos/${idx}/set-cover`);
      const next = [...photos];
      const [moved] = next.splice(idx, 1);
      next.unshift(moved);
      setPhotos(next);
      onChanged?.();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="property-photos-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><Camera className="w-5 h-5 text-indigo-500" /> Fotos de {property.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{photos.length}/10 · La primera foto es la portada</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {error && <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}
          {photos.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aún no hay fotos.</p>
              <p className="text-xs mt-1">Sube al menos 1 foto real de esta propiedad.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((p, idx) => (
                <div key={`${p.uploaded_at || ''}-${idx}`} className="relative group aspect-video bg-slate-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-indigo-300">
                  <img src={p.data_url} alt={`foto-${idx}`} className="w-full h-full object-cover cursor-pointer" onClick={() => setViewIdx(idx)} />
                  {idx === 0 && <span className="absolute top-1.5 left-1.5 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">PORTADA</span>}
                  <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                    {idx !== 0 && <button onClick={() => setAsCover(idx)} className="bg-indigo-500 hover:bg-indigo-600 text-white p-1.5 rounded-md" title="Poner como portada"><Home className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => removePhoto(idx)} className="bg-rose-500 hover:bg-rose-600 text-white p-1.5 rounded-md" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center gap-3">
          <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${photos.length >= 10 || uploading ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {uploading ? 'Subiendo...' : 'Subir foto'}
            <input type="file" accept="image/*" onChange={handleFile} disabled={photos.length >= 10 || uploading} className="hidden" />
          </label>
          <span className="text-xs text-slate-500">Máx 10 fotos. Se comprimen automáticamente.</span>
        </div>
      </div>
      {viewIdx !== null && photos[viewIdx] && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setViewIdx(null)}>
          <img src={photos[viewIdx].data_url} alt="ampliada" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2" onClick={() => setViewIdx(null)}><X className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );
}

function MergePropertyModal({ source, properties, bookingCount, onClose, onDone }) {
  const [targetId, setTargetId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const others = properties.filter(p => p.id !== source.id);
  const submit = async (e) => {
    e.preventDefault();
    if (!targetId) return;
    const target = properties.find(p => p.id === targetId);
    if (!window.confirm(`¿Fusionar "${source.name}" con "${target.name}"?\n\nSe moverán todas las reservas de "${source.name}" a "${target.name}" y luego se eliminará "${source.name}". Esta acción no se puede deshacer.`)) return;
    setError(''); setLoading(true);
    try {
      const { data } = await api.post(`/properties/${source.id}/merge-into/${targetId}`);
      setResult(data);
      setTimeout(() => onDone(), 2000);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || 'Error'); } finally { setLoading(false); }
  };
  return (
    <Modal title="Fusionar propiedades" icon={<AlertTriangle className="w-5 h-5 text-amber-500" />} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        {result ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-center"><CheckCircle2 className="w-8 h-8 mx-auto mb-2" /><p className="font-semibold">¡Fusión completada!</p><p className="text-sm mt-1">{result.bookings_moved} reservas + {result.tasks_moved} tareas movidas a "{result.target_name}"</p></div>
        ) : (
          <>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-semibold mb-1">📦 Propiedad origen: {source.name}</p>
              <p className="text-xs">Tiene {bookingCount} reserva{bookingCount === 1 ? '' : 's'} y será eliminada tras la fusión.</p>
            </div>
            <Field label="Fusionar con (propiedad destino)">
              <select data-testid="merge-target-select" required value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="" disabled>Selecciona la propiedad que se queda</option>
                {others.map(p => <option key={p.id} value={p.id}>{p.name}{p.owner_name ? ` — ${p.owner_name}` : ''}</option>)}
              </select>
            </Field>
            <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">Todas las reservas, tareas de limpieza y check-ins de <b>{source.name}</b> pasarán a la propiedad destino. Después, <b>{source.name}</b> quedará eliminada.</div>
            {error && <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}
            <div className="pt-4 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button data-testid="confirm-merge-button" type="submit" disabled={loading || !targetId} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-60">{loading ? 'Fusionando...' : 'Confirmar fusión'}</button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}

function ChangePasswordModal({ onClose }) {
  const [currentPw, setCurrentPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { setError('Mínimo 6 caracteres'); return; }
    if (newPw !== confirm) { setError('Las contraseñas no coinciden'); return; }
    setError(''); setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || 'Error'); } finally { setLoading(false); }
  };
  return (
    <Modal title="Cambiar contraseña" icon={<LockKeyhole className="w-5 h-5 text-indigo-500" />} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        {success ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-center"><CheckCircle2 className="w-8 h-8 mx-auto mb-2" /><p className="font-semibold">Contraseña actualizada</p></div>
        ) : (
          <>
            <Field label="Contraseña actual"><input data-testid="current-password" type="password" required value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <Field label="Nueva contraseña (mín 6 caracteres)"><input data-testid="new-password" type="password" required minLength="6" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            <Field label="Confirmar nueva contraseña"><input data-testid="confirm-password" type="password" required minLength="6" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
            {error && <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}
            <div className="pt-4 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button data-testid="save-password" type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-60">{loading ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}

function EditUserModal({ editing, onSave, onDelete, onClose }) {
  const [name, setName] = React.useState(editing.user.name || '');
  const [email, setEmail] = React.useState(editing.user.email || '');
  const [phone, setPhone] = React.useState(editing.user.phone || '');
  const roleLabel = editing.role === 'owner' ? 'Propietario' : editing.role === 'cleaner' ? 'Limpiador/a' : 'Recepcionista';
  const submit = (e) => { e.preventDefault(); onSave({ name, email, phone }); };
  return (
    <Modal title={`Editar ${roleLabel}`} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        <Field label="Nombre Completo"><input data-testid="edit-user-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <Field label="Email (login)"><input data-testid="edit-user-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <Field label="Teléfono"><input data-testid="edit-user-phone" type="tel" placeholder="+34..." value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" /></Field>
        <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">⚠️ Si cambias el email, esta persona deberá usar el nuevo email para iniciar sesión. La contraseña no cambia.</div>
        <div className="pt-4 flex flex-wrap justify-between items-center gap-3">
          <button data-testid="delete-user-button" type="button" onClick={onDelete} className="px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 rounded-lg flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Eliminar</button>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button data-testid="save-edit-user-button" type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">Guardar Cambios</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// Live-updating timer that ticks every second when task is running
function TaskTimer({ task, size = 'md' }) {
  const [, setTick] = React.useState(0);
  const segments = task.time_segments || [];
  const { seconds, isRunning } = computeElapsed(segments);
  React.useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);
  const textSize = size === 'lg' ? 'text-2xl' : 'text-base';
  return (
    <span className={`font-mono font-bold tabular-nums ${textSize} ${isRunning ? 'text-amber-600' : 'text-slate-700'}`} data-testid={`timer-${task.id}`}>
      {formatClock(seconds)}
    </span>
  );
}

function TaskActions({ task, canManage, onStart, onPause, onFinish, onOpenPhotos }) {
  const segments = task.time_segments || [];
  const hasOpenSegment = segments.length > 0 && !segments[segments.length - 1].end;
  const hasAnySegment = segments.length > 0;
  const photoCount = (task.photos || []).length;
  const { seconds } = computeElapsed(segments);

  if (task.status === 'completed') {
    return (
      <div className="flex flex-col items-start gap-0.5">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Completada</span>
        {seconds > 0 && <span className="text-[11px] text-slate-500">Duración: <span className="font-semibold text-slate-700">{formatDuration(seconds)}</span></span>}
      </div>
    );
  }

  if (!canManage) {
    // Read-only view (admin viewing a cleaner's task also managed, so this handles owner/checkin)
    return (
      <div className="flex flex-col items-start gap-0.5">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${hasOpenSegment ? 'bg-amber-100 text-amber-700' : hasAnySegment ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>
          {hasOpenSegment ? <><Clock className="w-3.5 h-3.5" /> En curso</> : hasAnySegment ? <><Pause className="w-3.5 h-3.5" /> Pausada</> : 'Pendiente'}
        </span>
        {hasAnySegment && <TaskTimer task={task} size="md" />}
      </div>
    );
  }

  // pending — no segments yet
  if (!hasAnySegment) {
    return (
      <button
        data-testid={`start-task-${task.id}`}
        onClick={() => onStart(task)}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm transition-colors"
      >
        <Play className="w-4 h-4 fill-current" /> Fichar llegada
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${hasOpenSegment ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
        <Timer className={`w-4 h-4 ${hasOpenSegment ? 'text-amber-600' : 'text-slate-500'}`} />
        <TaskTimer task={task} />
        {!hasOpenSegment && <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pausada</span>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {hasOpenSegment ? (
          <button data-testid={`pause-task-${task.id}`} onClick={() => onPause(task)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors">
            <Pause className="w-3.5 h-3.5" /> Pausar
          </button>
        ) : (
          <button data-testid={`resume-task-${task.id}`} onClick={() => onStart(task)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors">
            <Play className="w-3.5 h-3.5 fill-current" /> Reanudar
          </button>
        )}
        <button
          data-testid={`finish-task-${task.id}`}
          onClick={() => onFinish(task)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors bg-rose-600 hover:bg-rose-700 text-white"
          title="Finalizar tarea"
        >
          <Square className="w-3.5 h-3.5 fill-current" /> Finalizar
        </button>
      </div>
    </div>
  );
}
